import { organizations, users } from './auth-schema';
import { sql } from 'drizzle-orm';
import {
	boolean,
	check,
	foreignKey,
	index,
	integer,
	json,
	jsonb,
	numeric,
	pgEnum,
	pgTable,
	primaryKey,
	text,
	timestamp,
	unique,
	uniqueIndex,
	uuid,
	varchar
} from 'drizzle-orm/pg-core';

export const entityType = pgEnum('entityType', [
	'session',
	'sessionTrack',
	'speaker',
	'sponsor',
	'attendee',
	'contentItem',
	'ticket',
	'registrationType',
	'addon',
	'sessionPackage'
]);
export const integrationConnectionStatus = pgEnum('integration_connection_status', [
	'active',
	'revoked',
	'error'
]);
export const integrationSubscriptionStatus = pgEnum('integration_notification_subscription_status', [
	'enabled',
	'disabled',
	'auto_disabled'
]);
export const integrationDeliveryStatus = pgEnum('integration_notification_delivery_status', [
	'pending',
	'delivering',
	'succeeded',
	'failed',
	'exhausted'
]);
// paused_pending_backfill and circuit_open are declared before anything writes them
// (phase 4 and the delivery worker respectively) because adding an enum value later
// costs a migration and values are cheap.
export const integrationRecordSubscriptionStatus = pgEnum(
	'integration_record_subscription_status',
	['enabled', 'disabled', 'auto_disabled', 'paused_pending_backfill', 'circuit_open']
);
export const integrationRecordDeliveryStatus = pgEnum('integration_record_delivery_status', [
	'pending',
	'delivered',
	'failed',
	'dead_lettered',
	'undeliverable'
]);
export const exportBatchStatus = pgEnum('export_batch_status', [
	'pending',
	'processing',
	'completed',
	'failed',
	'partially_completed'
]);
export const exportJobStatus = pgEnum('export_job_status', ['pending', 'processing', 'completed', 'failed', 'skipped']);
export const exportJobType = pgEnum('export_job_type', ['attendees', 'speakers', 'sponsors', 'sessions', 'orders']);

export const checkInPointType = pgEnum('check_in_point_type', ['event', 'day', 'session']);

export const checkInDirection = pgEnum('check_in_direction', ['in', 'out']);

export const checkInSource = pgEnum('check_in_source', ['scan', 'search', 'kiosk', 'manual']);

export const checkInDeviceMode = pgEnum('check_in_device_mode', ['operator', 'kiosk']);

// When a ticket or add-on stops being purchasable. 'inherit' defers to the
// event's sales_close_default setting, so moving that setting moves every item
// that has not been given its own answer; 'custom' reads the date from
// availability_end; 'never' keeps it on sale indefinitely.
export const salesCloseMode = pgEnum('sales_close_mode', ['inherit', 'event_end', 'custom', 'never']);

export const tickets = pgTable(
	'tickets',
	{
		ticketId: uuid('ticket_id').defaultRandom().primaryKey().notNull(),
		ticketTypeId: uuid('ticket_type_id').notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id'),
		name: text().notNull(),
		description: text(),
		price: numeric({ precision: 10, scale: 2 }),
		isPublished: boolean('is_published').default(true).notNull(),
		totalQuantity: integer('total_quantity'),
		maxQuantityPerOrder: integer('max_quantity_per_order').default(1),
		minQuantityPerOrder: integer('min_quantity_per_order').default(1),
		displayOrder: integer('display_order'),
		availabilityStart: timestamp('availability_start', { mode: 'string' }),
		availabilityEnd: timestamp('availability_end', { mode: 'string' }),
		salesCloseMode: salesCloseMode('sales_close_mode').default('inherit').notNull(),
		config: jsonb(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		refundPolicyId: uuid('refund_policy_id')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'tickets_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.refundPolicyId],
			foreignColumns: [refundPolicies.refundPolicyId],
			name: 'tickets_refund_policies_refund_policy_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'tickets_organization_id_tenants_organization_id_fk'
		}),
		foreignKey({
			columns: [table.ticketTypeId],
			foreignColumns: [ticketTypes.ticketTypeId],
			name: 'tickets_ticket_type_id_fk'
		})
	]
);

export const refundPolicies = pgTable(
	'refund_policies',
	{
		refundPolicyId: uuid('refund_policy_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' })
			.default(sql`CURRENT_TIMESTAMP`)
			.notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
			.default(sql`CURRENT_TIMESTAMP`)
			.notNull(),
		isDefault: boolean('is_default').default(false)
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'refund_policies_event_id_fkey'
		}).onDelete('cascade')
	]
);

export const fieldTypes = pgTable('field_types', {
	fieldTypeId: uuid('field_type_id')
		.default(sql`gen_random_uuid()`)
		.primaryKey()
		.notNull(),
	key: text().notNull(),
	displayName: text('display_name').notNull(),
	description: text(),
	hasOptions: boolean('has_options').default(false),
	supportsValidation: boolean('supports_validation').default(false),
	supportsPlaceholder: boolean('supports_placeholder').default(true),
	supportsMultiple: boolean('supports_multiple').default(false),
	inputType: text('input_type'),
	createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' }).defaultNow(),
	updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' }).defaultNow(),
	isEnabled: boolean('is_enabled').default(true),
	parentFormFieldId: uuid('parent_form_field_id'),
	canBeChild: boolean('can_be_child').default(true),
	canBeParent: boolean('can_be_parent').default(true),
	allowedDependencyEvaluators: text('allowed_dependency_evaluators')
		.array()
		.default(sql`ARRAY['equals']::text[]`)
});

export const formFieldDependencies = pgTable(
	'form_field_dependencies',
	{
		formFieldDependencyId: uuid('form_field_dependency_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		formFieldId: uuid('form_field_id').notNull(),
		parentFieldId: uuid('parent_field_id').notNull(),
		conditionType: text('condition_type').notNull(),
		conditionValue: text('condition_value'),
		createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' }).defaultNow(),
		conditionOptionIds: uuid('condition_option_ids')
			.array()
			.default(sql`ARRAY[]::uuid[]`)
	},
	(table) => [
		index('idx_form_field_dependencies_parent').using('btree', table.parentFieldId.asc().nullsLast().op('uuid_ops')),
		foreignKey({
			columns: [table.formFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'form_field_dependencies_form_field_id_fkey'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.parentFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'form_field_dependencies_parent_field_id_fkey'
		}).onDelete('cascade'),
		check(
			'form_field_dependencies_condition_type_check',
			sql`condition_type = ANY (ARRAY['equals'::text, 'not_equals'::text, 'contains'::text, 'starts_with'::text, 'ends_with'::text])`
		)
	]
);

export const formFieldOptions = pgTable(
	'form_field_options',
	{
		formFieldOptionId: uuid('form_field_option_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		formFieldId: uuid('form_field_id'),
		label: text().notNull(),
		value: text().notNull(),
		displayOrder: integer('display_order').default(0).notNull(),
		createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' }).defaultNow(),
		deletedAt: timestamp('deleted_at', { mode: 'string' })
	},
	(table) => [
		foreignKey({
			columns: [table.formFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'form_field_options_form_field_id_fkey'
		}).onDelete('cascade')
	]
);

export const formFields = pgTable(
	'form_fields',
	{
		formFieldId: uuid('form_field_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id'),
		label: text().notNull(),
		fieldTypeKey: text('field_type_key'),
		isSystem: boolean('is_system').default(false),
		isPreset: boolean('is_preset').default(false),
		displayOrder: integer('display_order').default(0).notNull(),
		colStart: integer('col_start').default(1).notNull(),
		colEnd: integer('col_end').default(13).notNull(),
		layoutOptions: jsonb('layout_options').default({}),
		createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' }).defaultNow(),
		isRequired: boolean('is_required').default(false),
		description: text(),
		lookupEntity: text('lookup_entity'),
		placeholder: text(),
		name: text(),
		eventId: uuid('event_id'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		hiddenAt: timestamp('hidden_at', { mode: 'string' }),
		parentFormFieldId: uuid('parent_form_field_id') // Link to org-level parent question
	},
	(table) => [
		index('idx_form_fields_tenant').using('btree', table.organizationId.asc().nullsLast().op('uuid_ops')),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'form_fields_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.fieldTypeKey],
			foreignColumns: [fieldTypes.key],
			name: 'form_fields_field_type_key_fkey'
		}).onDelete('restrict'),
		foreignKey({
			columns: [table.parentFormFieldId],
			foreignColumns: [table.formFieldId],
			name: 'form_fields_parent_form_field_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'form_fields_organization_id_fkey'
		}).onDelete('cascade')
	]
);

export const formFieldVisibilityRules = pgTable(
	'form_field_visibility_rules',
	{
		visibilityRuleId: uuid('visibility_rule_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		formFieldId: uuid('form_field_id'),
		registrationTypeId: uuid('registration_type_id').notNull(),
		ticketId: uuid('ticket_id'),
		displayOrder: integer('display_order'),
		createAt: timestamp('create_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		index('form_field_visibility_rules_key_index').using(
			'btree',
			table.formFieldId.asc().nullsLast().op('uuid_ops'),
			table.registrationTypeId.asc().nullsLast().op('uuid_ops'),
			table.ticketId.asc().nullsLast().op('uuid_ops')
		),
		foreignKey({
			columns: [table.formFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'form_field_visibility_rules_form_fields_form_field_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'form_field_visibility_rules_registration_type_id_fk'
		}),
		foreignKey({
			columns: [table.ticketId],
			foreignColumns: [tickets.ticketId],
			name: 'form_field_visibility_rules_tickets_ticket_id_fk'
		})
	]
);

export const formFieldValidationRules = pgTable(
	'form_field_validation_rules',
	{
		validationRuleId: uuid('validation_rule_id').defaultRandom().primaryKey().notNull(),
		formFieldId: uuid('form_field_id').notNull(),
		validationType: varchar('validation_type', { length: 50 }).notNull(), // e.g., 'min', 'max', 'phoneFormat', 'errorMessage'
		validationValue: text('validation_value'), // Stored as text, cast to appropriate type when needed
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow()
	},
	(table) => [
		foreignKey({
			columns: [table.formFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'form_field_validation_rules_form_field_id_fk'
		}).onDelete('cascade')
	]
);

export const contentItems = pgTable(
	'content_items',
	{
		contentItemId: uuid('content_item_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		name: varchar({ length: 255 }).notNull(),
		content: text(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'content_items_event_id_events_event_id_fk'
		})
	]
);

export const events = pgTable(
	'events',
	{
		eventId: uuid('event_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventTypeId: uuid('event_type_id').notNull(),
		slug: varchar({ length: 100 }),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		startDate: timestamp('start_date', { mode: 'string' }).notNull(),
		endDate: timestamp('end_date', { mode: 'string' }),
		url: varchar({ length: 255 }),
		venueId: uuid('venue_id'),
		isPublished: boolean('is_published').default(false).notNull(),
		isPrivate: boolean('is_private').default(false).notNull(),
		isInternal: boolean('is_internal').default(false).notNull(),
		theme: json(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		ownerEmail: varchar('owner_email'),
		timezone: varchar(),
		uniqueCode: text('unique_code').generatedAlwaysAs(
			sql`upper(encode(SUBSTRING(decode(md5((event_id)::text), 'hex'::text) FROM 1 FOR 4), 'hex'::text))`
		),
		confirmationPrefixCode: varchar('confirmation_prefix_code', { length: 16 }).unique()
	},
	(table) => [
		uniqueIndex('events_unique_code_uindex').using('btree', table.uniqueCode.asc().nullsLast().op('text_ops')),
		foreignKey({
			columns: [table.eventTypeId],
			foreignColumns: [eventTypes.eventTypeId],
			name: 'events_event_type_id_event_types_event_type_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'events_organization_id_tenants_organization_id_fk'
		}),
		foreignKey({
			columns: [table.venueId],
			foreignColumns: [venues.venueId],
			name: 'events_venue_id_venues_venue_id_fk'
		}),
		unique('events_slug_unique').on(table.slug)
	]
);

export const eventBranding = pgTable(
	'event_branding',
	{
		eventBrandingId: uuid('event_branding_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),

		// JSON column for flexible branding configuration
		brandingConfig: jsonb('branding_config').$type<
			| {
					colors: {
						light: {
							primary: string;
							primaryForeground: string;
							secondary: string;
							secondaryForeground: string;
							background: string;
							foreground: string;
							destructive: string;
							destructiveForeground: string;
							info: string;
							infoForeground: string;
							layout: string;
							layoutForeground: string;
							muted?: string;
							mutedForeground?: string;
							accent?: string;
							accentForeground?: string;
							border?: string;
							input?: string;
							ring?: string;
							warning?: string;
							warningForeground?: string;
							success?: string;
							successForeground?: string;
						};
						dark?: {
							primary?: string;
							primaryForeground?: string;
							secondary?: string;
							secondaryForeground?: string;
							background?: string;
							foreground?: string;
							destructive?: string;
							destructiveForeground?: string;
							info?: string;
							infoForeground?: string;
							layout?: string;
							layoutForeground?: string;
							muted?: string;
							mutedForeground?: string;
							accent?: string;
							accentForeground?: string;
							border?: string;
							input?: string;
							ring?: string;
							warning?: string;
							warningForeground?: string;
							success?: string;
							successForeground?: string;
						};
					};
					typography?: {
						fontFamily?: { primary?: string; heading?: string; mono?: string };
						fontWeight?: { normal?: number; medium?: number; semibold?: number; bold?: number };
						customCss?: string;
					};
					interactions?: {
						animations?: { enabled?: boolean; duration?: 'fast' | 'normal' | 'slow' };
						feedback?: { haptic?: boolean; sound?: boolean };
					};
					customCss?: string;
					// Image URLs stored temporarily in config (TODO: migrate to eventBrandingImages/eventBrandingImageAssignments)
					logoUrl?: string | null;
					heroImageUrl?: string | null;
					version?: string;
			  }
			| {
					version: string;
					appearance: 'light' | 'dark';
					colors: { brand: string };
					typography?: {
						fontFamily?: { primary?: string; heading?: string; mono?: string };
						fontWeight?: { normal?: number; medium?: number; semibold?: number; bold?: number };
						customCss?: string;
					};
					interactions?: {
						animations?: { enabled?: boolean; duration?: 'fast' | 'normal' | 'slow' };
						feedback?: { haptic?: boolean; sound?: boolean };
					};
					customCss?: string;
					logoUrl?: string | null;
					heroImageUrl?: string | null;
			  }
		>(),

		// Metadata
		isActive: boolean('is_active').default(true).notNull(),
		// Every event is seeded with a default branding row, so the row's existence
		// says nothing about whether anyone has branded the event. Only a save from
		// a branding surface stamps this.
		configuredAt: timestamp('configured_at'),
		createdAt: timestamp('created_at').defaultNow().notNull(),
		updatedAt: timestamp('updated_at').defaultNow().notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'event_branding_event_id_events_event_id_fk'
		}),
		unique('event_branding_event_id_unique').on(table.eventId)
	]
);

export const eventBrandingImages = pgTable(
	'event_branding_images',
	{
		imageId: uuid('image_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		organizationId: text('organization_id').notNull(),

		// File information
		url: varchar('url', { length: 500 }).notNull(),
		filename: varchar('filename', { length: 255 }).notNull(),
		mimeType: varchar('mime_type', { length: 100 }).notNull(),
		fileSize: integer('file_size'),
		width: integer('width'),
		height: integer('height'),
		altText: text('alt_text'),

		// Metadata
		uploadedBy: text('uploaded_by').notNull(),
		createdAt: timestamp('created_at').defaultNow().notNull(),
		updatedAt: timestamp('updated_at').defaultNow().notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'event_branding_images_event_id_events_event_id_fk'
		}),
		index('event_branding_images_event_id_idx').on(table.eventId)
	]
);

export const eventBrandingImageAssignments = pgTable(
	'event_branding_image_assignments',
	{
		assignmentId: uuid('assignment_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		imageId: uuid('image_id').notNull(),

		// Assignment configuration
		purpose: varchar('purpose', { length: 50 }).notNull(), // 'logo', 'hero', 'background', 'social_og', etc.
		colorMode: varchar('color_mode', { length: 10 }).default('all').notNull(), // 'light', 'dark', 'all'
		deviceType: varchar('device_type', { length: 20 }).default('all').notNull(), // 'desktop', 'mobile', 'tablet', 'all'

		// Metadata
		isActive: boolean('is_active').default(true).notNull(),
		sortOrder: integer('sort_order').default(0).notNull(),
		createdAt: timestamp('created_at').defaultNow().notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'event_branding_image_assignments_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.imageId],
			foreignColumns: [eventBrandingImages.imageId],
			name: 'event_branding_image_assignments_image_id_event_branding_images_image_id_fk'
		}),
		unique('event_branding_image_assignments_unique').on(
			table.eventId,
			table.purpose,
			table.colorMode,
			table.deviceType
		),
		index('event_branding_image_assignments_event_id_idx').on(table.eventId),
		index('event_branding_image_assignments_image_id_idx').on(table.imageId)
	]
);

export const eventTypes = pgTable(
	'event_types',
	{
		eventTypeId: uuid('event_type_id').defaultRandom().primaryKey().notNull(),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		styles: jsonb(),
		lookupKey: varchar('lookup_key')
	},
	(table) => [unique('event_types_lookup_key_key').on(table.lookupKey)]
);

export const attendees = pgTable(
	'attendees',
	{
		attendeeId: uuid('attendee_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		userId: text('user_id'),
		firstName: varchar('first_name', { length: 255 }),
		lastName: varchar('last_name', { length: 255 }),
		displayName: varchar('display_name', { length: 255 }),
		profilePicture: varchar('profile_picture', { length: 255 }),
		title: varchar({ length: 255 }),
		company: varchar({ length: 255 }),
		bio: text(),
		extraProfileData: json('extra_profile_data'),
		registrationTypeId: uuid('registration_type_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		ticketId: uuid('ticket_id'),
		completedRegistrationAt: timestamp('completed_registration_at', { mode: 'string' }),
		activeRegistrationStatusId: uuid('active_registration_status_id'),
		email: varchar('email', { length: 255 }),
		ownershipUserId: text('owner_ship_user_id'),
		contactId: uuid('contact_id')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'attendees_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'attendees_registration_type'
		}),
		foreignKey({
			columns: [table.ticketId],
			foreignColumns: [tickets.ticketId],
			name: 'attendees_tickets_fk'
		}),
		foreignKey({
			columns: [table.userId],
			foreignColumns: [users.id],
			name: 'attendees_user_id_users_user_id_fk'
		}),
		// attendees_uk and attendees_email_uk are partial-unique indexes
		// (WHERE deleted_at IS NULL) — defined in migration 0053. Drizzle's
		// schema DSL doesn't model partial uniques, so they exist only in SQL.
		index('attendees_user_id_idx').on(table.userId),
		index('attendees_event_id_idx').on(table.eventId)
	]
);

export const orders = pgTable(
	'orders',
	{
		orderId: uuid('order_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		userId: text('user_id').notNull(),
		attendeeId: uuid('attendee_id'),
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id'),
		// totalAmount = the order's gross at creation time (sum of orderItems
		// totalPrice for purchases). It is NEVER updated when items are
		// removed (orderItems.removedAt) — those refunds are tracked at the
		// payment level via payments.refundedAmount. To get "what is this
		// attendee's current liability for this order", compute:
		//   sum(orderItems WHERE removedAt IS NULL) - sum(payments.refundedAmount)
		// rather than reading totalAmount. Reports that sum totalAmount
		// across paid orders give "gross billed", which is usually NOT what
		// "revenue" or "amount paid" should report after refunds.
		totalAmount: numeric('total_amount', { precision: 10, scale: 2 }).notNull(),
		lifecycleState: varchar('lifecycle_state', { length: 20 }).default('ACTIVE').notNull(),
		paymentState: varchar('payment_state', { length: 20 }).default('UNPAID').notNull(),
		commitmentState: varchar('commitment_state', { length: 20 }).default('CONFIRMED').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		paidAt: timestamp('paid_at', { mode: 'string' }),
		paymentMethod: varchar('payment_method', { length: 100 }),
		paidBy: uuid('paid_by'),
		paymentToken: varchar('payment_token', { length: 64 }).unique().notNull(),
		pendingChanges: jsonb('pending_changes'),
		adminNote: text('admin_note'),
		smokeTestRunId: uuid('smoke_test_run_id')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'orders_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.userId],
			foreignColumns: [users.id],
			name: 'orders_user_fk'
		}),
		index('orders_event_id_idx').on(table.eventId),
		index('orders_attendee_id_idx').on(table.attendeeId),
		index('orders_lifecycle_payment_idx').on(table.lifecycleState, table.paymentState),
		index('orders_smoke_test_run_id_idx')
			.on(table.smokeTestRunId)
			.where(sql`smoke_test_run_id IS NOT NULL`)
	]
);

export const attendeeRegistrationStatuses = pgTable(
	'attendee_registration_statuses',
	{
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		attendeeId: uuid('attendee_id'),
		status: varchar({ length: 100 }).notNull(),
		lastStep: uuid('last_step'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		emailValidationId: uuid('email_validation_id'),
		confirmationCode: varchar('confirmation_code', { length: 50 }),
		linkedRegistrationTypeId: uuid('linked_registration_type_id'),
		cancelledAt: timestamp('cancelled_at', { mode: 'string' }),
		cancelledBy: text('cancelled_by'),
		cancellationReason: text('cancellation_reason')
	},
	(table) => [
		foreignKey({
			columns: [table.attendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'attendee_registration_statuses_attendees_attendee_id_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'attendee_registration_statuses_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.lastStep],
			foreignColumns: [registrationFormSteps.registrationFormStepId],
			name: 'attendee_registration_statuses_last_step_fk'
		}),
		foreignKey({
			columns: [table.linkedRegistrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'attendee_registration_statuses_linked_registration_type_id_fk'
		}),
		index('attendee_registration_statuses_event_id_idx').on(table.eventId),
		index('attendee_registration_statuses_attendee_id_idx').on(table.attendeeId)
	]
);

export const attendeeRegistrationEmailValidations = pgTable(
	'attendee_registration_email_validations',
	{
		attendeeRegistrationEmailValidationId: uuid('attendee_registration_email_validation_id')
			.defaultRandom()
			.primaryKey()
			.notNull(),
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id').notNull(),
		eventId: uuid('event_id').notNull(),
		email: varchar('email', { length: 255 }).notNull(),
		token: varchar('token', { length: 255 }).notNull().unique(),
		expiresAt: timestamp('expires_at', { mode: 'string' }).notNull(),
		validatedAt: timestamp('validated_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow()
	},
	(table) => [
		foreignKey({
			columns: [table.attendeeRegistrationStatusId],
			foreignColumns: [attendeeRegistrationStatuses.attendeeRegistrationStatusId],
			name: 'attendee_registration_email_validations_attendee_registration_statuses_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'attendee_registration_email_validations_events_event_id_fk'
		})
	]
);

export const attendeeChangeLogs = pgTable(
	'attendee_change_logs',
	{
		id: uuid('id').defaultRandom().primaryKey().notNull(),
		attendeeId: uuid('attendee_id').notNull(),
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id').notNull(),
		eventId: uuid('event_id').notNull(),
		changeType: varchar('change_type', { length: 50 }).notNull(),
		previousState: jsonb('previous_state'),
		newState: jsonb('new_state'),
		changedByUserId: text('changed_by_user_id'),
		changedByUserType: varchar('changed_by_user_type', { length: 20 }).notNull(),
		changedAt: timestamp('changed_at', { mode: 'string' }).defaultNow().notNull(),
		notes: text('notes')
	},
	(table) => [
		foreignKey({
			columns: [table.attendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'attendee_change_logs_attendee_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.attendeeRegistrationStatusId],
			foreignColumns: [attendeeRegistrationStatuses.attendeeRegistrationStatusId],
			name: 'attendee_change_logs_attendee_registration_status_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'attendee_change_logs_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.changedByUserId],
			foreignColumns: [users.id],
			name: 'attendee_change_logs_changed_by_user_id_fk'
		})
	]
);

export const registrationFormSteps = pgTable('registration_form_steps', {
	registrationFormStepId: uuid('registration_form_step_id').defaultRandom().primaryKey().notNull(),
	stepSlug: varchar('step_slug', { length: 100 }).notNull(),
	stepType: varchar('step_type', { length: 100 }).notNull(),
	title: varchar({ length: 500 }),
	description: text(),
	stepConfig: json('step_config'),
	ordinal: integer().default(99999).notNull(),
	createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
	createdBy: integer('created_by'),
	updatedAt: timestamp('updated_at', { mode: 'string' }),
	updatedBy: integer('updated_by'),
	deletedAt: timestamp('deleted_at', { mode: 'string' }),
	deletedBy: text('deleted_by'),
	reportingCode: varchar('reporting_code', { length: 255 }),
	eventId: uuid('event_id'),
	shortName: varchar('short_name'),
	triggerEmailUsageKey: varchar('trigger_email_usage_key'),
	icon: varchar('icon', { length: 255 })
});

export const attendeeRegistrationValues = pgTable(
	'attendee_registration_values',
	{
		attendeeRegistrationValueId: uuid('attendee_registration_value_id').defaultRandom().primaryKey().notNull(),
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id').notNull(),
		value: jsonb(),
		fieldOptionId: uuid('field_option_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		eventId: uuid('event_id').notNull(),
		formFieldId: uuid('form_field_id'),
		fieldKey: varchar('field_key', { length: 200 })
	},
	(table) => [
		foreignKey({
			columns: [table.attendeeRegistrationStatusId],
			foreignColumns: [attendeeRegistrationStatuses.attendeeRegistrationStatusId],
			name: 'attendee_registration_values_attendee_registration_statuses_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'attendee_registration_values_events_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.formFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'attendee_registration_values_form_fields_form_field_id_fk'
		})
	]
);

export const media = pgTable(
	'media',
	{
		mediaId: uuid('media_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		mediaType: varchar('media_type', { length: 100 }).notNull(),
		mediaUrl: varchar('media_url', { length: 255 }).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'media_event_id_events_event_id_fk'
		})
	]
);

export const payments = pgTable(
	'payments',
	{
		paymentId: uuid('payment_id').defaultRandom().primaryKey().notNull(),
		orderId: uuid('order_id'),
		paymentDate: timestamp('payment_date', { mode: 'string' }).defaultNow(),
		amount: numeric({ precision: 10, scale: 2 }).notNull(),
		paymentMethod: varchar('payment_method', { length: 100 }),
		paymentStatus: varchar('payment_status', { length: 100 }).default('PENDING'),
		transactionId: varchar('transaction_id', { length: 255 }),
		processorName: varchar('processor_name', { length: 100 }).notNull(),
		processorStatus: varchar('processor_status', { length: 50 }),
		responseData: json('response_data'),

		// Stripe-specific fields
		stripePaymentIntentId: varchar('stripe_payment_intent_id', { length: 255 }),
		stripeChargeId: varchar('stripe_charge_id', { length: 255 }),
		stripeFeeAmount: numeric('stripe_fee_amount', { precision: 10, scale: 2 }),
		sunfishFeeAmount: numeric('sunfish_fee_amount', { precision: 10, scale: 2 }),
		plannerNetAmount: numeric('planner_net_amount', { precision: 10, scale: 2 }),
		refundedAmount: numeric('refunded_amount', { precision: 10, scale: 2 }).default('0').notNull(),
		stripeRefundId: varchar('stripe_refund_id', { length: 255 }),
		refundReason: text('refund_reason'),

		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.orderId],
			foreignColumns: [orders.orderId],
			name: 'payments_order_id_orders_order_id_fk'
		}),
		// One live row per Stripe intent. PaymentIntent creation is already
		// idempotent on its own key, so two concurrent checkout submits get the
		// SAME intent back from Stripe and then both inserted a row for it: a
		// check-then-act race with nothing enforcing the "then". Both rows go on
		// to mirror the same status and refundedAmount, so every total that sums
		// payments counts the money twice. Partial, because soft-deleted rows are
		// history and may legitimately repeat.
		uniqueIndex('payments_live_stripe_payment_intent_uk')
			.on(table.orderId, table.stripePaymentIntentId)
			.where(sql`${table.deletedAt} IS NULL AND ${table.stripePaymentIntentId} IS NOT NULL`)
	]
);

// One row per refund actually issued. payments.refundedAmount is a running
// total and cannot say what any part of it was for, which left the dashboard
// unable to tell a cancellation refund from one a swap or modification already
// expressed as a credit line, and double-counting the difference (SUN-821).
export const paymentRefunds = pgTable(
	'payment_refunds',
	{
		paymentRefundId: uuid('payment_refund_id').defaultRandom().primaryKey().notNull(),
		paymentId: uuid('payment_id').notNull(),
		orderId: uuid('order_id'),
		// The modification that caused the refund, which is rarely the order the
		// money came off: refunds drain the newest payments first, so a downgrade
		// is funded by whichever earlier order still had cash on it. Without this
		// the ledger could only show the refund against the drained payment, with
		// nothing on the change that triggered it (SUN-859 follow-up).
		sourceOrderId: uuid('source_order_id'),
		amount: numeric({ precision: 10, scale: 2 }).notNull(),
		// 'cancellation' | 'order_modification', from the refund engine's caller.
		reason: varchar('reason', { length: 50 }).notNull(),
		note: text('note'),
		stripeRefundId: varchar('stripe_refund_id', { length: 255 }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by')
	},
	(table) => [
		// Cascaded: these rows only describe their payment, so a hard delete of it
		// leaves nothing for them to mean. Real payments are soft-deleted, so this
		// only fires where rows are genuinely torn down (the smoke-test harness).
		foreignKey({
			columns: [table.paymentId],
			foreignColumns: [payments.paymentId],
			name: 'payment_refunds_payment_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.orderId],
			foreignColumns: [orders.orderId],
			name: 'payment_refunds_order_id_fk'
		}).onDelete('cascade'),
		// Set null rather than cascade: losing the causing order must not take
		// the record of the money with it.
		foreignKey({
			columns: [table.sourceOrderId],
			foreignColumns: [orders.orderId],
			name: 'payment_refunds_source_order_id_fk'
		}).onDelete('set null'),
		index('payment_refunds_order_idx').on(table.orderId),
		index('payment_refunds_source_order_idx').on(table.sourceOrderId),
		index('payment_refunds_payment_idx').on(table.paymentId)
	]
);

export const processedWebhookEvents = pgTable(
	'processed_webhook_events',
	{
		id: uuid('id').defaultRandom().primaryKey().notNull(),
		stripeEventId: varchar('stripe_event_id', { length: 255 }).notNull().unique(),
		eventType: varchar('event_type', { length: 100 }).notNull(),
		orderId: uuid('order_id'),
		paymentId: uuid('payment_id'),
		processedAt: timestamp('processed_at', { mode: 'string' }).defaultNow().notNull(),
		metadata: json('metadata')
	},
	(table) => [
		foreignKey({
			columns: [table.orderId],
			foreignColumns: [orders.orderId],
			name: 'processed_webhook_events_order_id_fk'
		})
	]
);

export const agendaSessions = pgTable(
	'agenda_sessions',
	{
		agendaSessionId: uuid('agenda_session_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		linkedAgendaSessionId: uuid('linked_session_id'),
		name: varchar({ length: 255 }).notNull(),
		teaser: varchar({ length: 255 }),
		description: text(),
		sessionType: varchar('session_type', { length: 100 }),
		startDate: timestamp('start_date', { mode: 'string' }).notNull(),
		endDate: timestamp('end_date', { mode: 'string' }).notNull(),
		capacity: integer(),
		locationId: uuid('location_id'),
		ticketId: uuid('ticket_id'),
		registrationRequired: boolean('registration_required').default(false),
		allowWaitlist: boolean('allow_waitlist').default(false),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'sessions_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.linkedAgendaSessionId],
			foreignColumns: [table.agendaSessionId],
			name: 'sessions_linked_session_id_sessions_session_id_fk'
		}),
		foreignKey({
			columns: [table.locationId],
			foreignColumns: [locations.locationId],
			name: 'sessions_location_id_locations_location_id_fk'
		})
	]
);

export const tags = pgTable(
	'tags',
	{
		tagId: uuid('tag_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		name: varchar({ length: 50 }).notNull(),
		description: varchar({ length: 1024 }),
		entityType: entityType('entity_type'),
		color: varchar({ length: 50 }),
		ordinal: integer().default(99999).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'tags_event_id_events_event_id_fk'
		})
	]
);

export const speakerTypes = pgTable(
	'speaker_types',
	{
		speakerTypeId: uuid('speaker_type_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		name: varchar({ length: 50 }).notNull(),
		description: text(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'speaker_types_event_id_events_event_id_fk'
		})
	]
);

export const speakers = pgTable(
	'speakers',
	{
		speakerId: uuid('speaker_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		firstName: varchar('first_name', { length: 255 }),
		lastName: varchar('last_name', { length: 255 }),
		jobTitle: varchar('job_title', { length: 255 }),
		company: varchar({ length: 255 }),
		bio: text(),
		photoKey: text('photo_key'),
		socials: jsonb().$type<{ x?: string; linkedin?: string; website?: string }>(),
		featured: boolean().default(false).notNull(),
		// A speaker is not necessarily a registered attendee, so this stays optional.
		attendeeId: uuid('attendee_id'),
		speakerTypeId: uuid('speaker_type_id'),
		ordinal: integer().default(99999).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'speakers_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.attendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'speaker_attendee_fk'
		}),
		foreignKey({
			columns: [table.speakerTypeId],
			foreignColumns: [speakerTypes.speakerTypeId],
			name: 'speaker_speaker_type_fk'
		}),
		index('speakers_event_idx').on(table.eventId)
	]
);

export const sessionSpeakers = pgTable(
	'session_speakers',
	{
		agendaSessionId: uuid('agenda_session_id').notNull(),
		speakerId: uuid('speaker_id').notNull(),
		ordinal: integer().default(99999).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'session_speaker_session_fk'
		}),
		foreignKey({
			columns: [table.speakerId],
			foreignColumns: [speakers.speakerId],
			name: 'session_speaker_speaker_fk'
		}),
		primaryKey({
			columns: [table.agendaSessionId, table.speakerId],
			name: 'session_speakers_session_id_speaker_id_pk'
		})
	]
);

export const sponsorTiers = pgTable(
	'sponsor_tiers',
	{
		sponsorTierId: uuid('sponsor_tier_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		name: varchar({ length: 100 }).notNull(),
		ordinal: integer().default(99999).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'sponsor_tiers_event_id_events_event_id_fk'
		}),
		index('sponsor_tiers_event_idx').on(table.eventId)
	]
);

export const sponsors = pgTable(
	'sponsors',
	{
		sponsorId: uuid('sponsor_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		name: varchar({ length: 255 }).notNull(),
		logoKey: text('logo_key'),
		url: text(),
		sponsorTierId: uuid('sponsor_tier_id'),
		ordinal: integer().default(99999).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'sponsors_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.sponsorTierId],
			foreignColumns: [sponsorTiers.sponsorTierId],
			name: 'sponsors_sponsor_tier_fk'
		}),
		index('sponsors_event_idx').on(table.eventId)
	]
);

export const sessionSponsors = pgTable(
	'session_sponsors',
	{
		agendaSessionId: uuid('agenda_session_id').notNull(),
		sponsorId: uuid('sponsor_id').notNull(),
		ordinal: integer().default(99999).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'session_sponsor_session_fk'
		}),
		foreignKey({
			columns: [table.sponsorId],
			foreignColumns: [sponsors.sponsorId],
			name: 'session_sponsor_sponsor_fk'
		}),
		primaryKey({
			columns: [table.agendaSessionId, table.sponsorId],
			name: 'session_sponsors_session_id_sponsor_id_pk'
		})
	]
);

export const venues = pgTable(
	'venues',
	{
		venueId: uuid('venue_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		type: varchar({ length: 100 }).default('physical').notNull(),
		name: varchar({ length: 255 }).notNull(),
		address: text(),
		city: varchar({ length: 100 }),
		state: varchar({ length: 100 }),
		country: varchar({ length: 100 }),
		timezone: varchar({ length: 50 }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		placeId: varchar('place_id', { length: 500 })
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'venues_organization_id_tenants_organization_id_fk'
		})
	]
);

export const locations = pgTable(
	'locations',
	{
		locationId: uuid('location_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		isPublished: boolean('is_published').default(true).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'locations_organization_id_tenants_organization_id_fk'
		})
	]
);

export const ticketTypes = pgTable(
	'ticket_types',
	{
		ticketTypeId: uuid('ticket_type_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id'),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		lookupKey: varchar('lookup_key')
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'ticket_types_organization_id_tenants_organization_id_fk'
		})
	]
);

export const orderItems = pgTable(
	'order_items',
	{
		orderItemId: uuid('order_item_id').defaultRandom().primaryKey().notNull(),
		orderId: uuid('order_id').notNull(),
		entityType: entityType('entity_type'),
		entityId: uuid('entity_id'),
		entityLabel: varchar('entity_label', { length: 255 }),
		pricePerItem: numeric('price_per_item', { precision: 10, scale: 2 }).notNull(),
		totalPrice: numeric('total_price', { precision: 10, scale: 2 }).notNull(),
		quantity: integer().default(1).notNull(),
		lineItemType: varchar('line_item_type', { length: 20 }).default('purchase').notNull(),
		registrationTypeId: uuid('registration_type_id'),
		// On credit rows (lineItemType='credit'), points to the order containing
		// the original purchase being netted out. Null on purchase/waiver rows.
		// Lets the drain-fee math at refund time group credits by their source
		// order without entityId-lookup ambiguity (an entity can have purchases
		// across multiple orders after re-buy chains).
		sourceOrderId: uuid('source_order_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`),
		removedAt: timestamp('removed_at', { mode: 'string' }),
		removedBy: text('removed_by'),
		discountAllocation: jsonb('discount_allocation'),
		isPoolLine: boolean('is_pool_line').default(false).notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.orderId],
			foreignColumns: [orders.orderId],
			name: 'order_items_order_id_orders_order_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.sourceOrderId],
			foreignColumns: [orders.orderId],
			name: 'order_items_source_order_id_orders_order_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'order_items_registration_type_id_fk'
		})
		// order_items_uk is a partial-unique index (WHERE removed_at IS NULL)
		// defined in migration 0139. Drizzle's schema DSL doesn't model partial
		// uniques. The active-only constraint lets a customer re-purchase an
		// entity_id after the original was removed (orderItems.removedAt set).
	]
);

export const orderItemSlots = pgTable(
	'order_item_slots',
	{
		orderItemSlotId: uuid('order_item_slot_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		buyerUserId: text('buyer_user_id').notNull(),
		registrationTypeId: uuid('registration_type_id').notNull(),
		status: varchar('status', { length: 20 }).default('OPEN').notNull(),
		eventInviteId: uuid('event_invite_id'),
		claimedByAttendeeId: uuid('claimed_by_attendee_id'),
		claimedAt: timestamp('claimed_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		// Ending a place dissolves it, and dissolving unlinks whoever held it, so
		// the row that remains cannot say what happened. These three are the
		// planner's record of it: set only when a place ends for good — a
		// cancellation or a refund — never when one is dissolved for bookkeeping.
		cancelledAt: timestamp('cancelled_at', { mode: 'string' }),
		cancelledBy: text('cancelled_by'),
		cancelledClaimantAttendeeId: uuid('cancelled_claimant_attendee_id')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'order_item_slots_event_id_fk'
		}),
		foreignKey({
			columns: [table.buyerUserId],
			foreignColumns: [users.id],
			name: 'order_item_slots_buyer_user_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'order_item_slots_registration_type_id_fk'
		}),
		foreignKey({
			columns: [table.eventInviteId],
			foreignColumns: [eventInvites.eventInviteId],
			name: 'order_item_slots_event_invite_id_fk'
		}),
		foreignKey({
			columns: [table.claimedByAttendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'order_item_slots_claimed_by_attendee_id_fk'
		}),
		foreignKey({
			columns: [table.cancelledClaimantAttendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'order_item_slots_cancelled_claimant_attendee_id_fk'
		}),
		uniqueIndex('order_item_slots_event_invite_uindex')
			.on(table.eventInviteId)
			.where(sql`event_invite_id IS NOT NULL AND deleted_at IS NULL`),
		index('order_item_slots_event_buyer_idx').on(table.eventId, table.buyerUserId),
		index('order_item_slots_claimed_by_idx').on(table.claimedByAttendeeId)
	]
);

export const orderItemSlotItems = pgTable(
	'order_item_slot_items',
	{
		orderItemSlotItemId: uuid('order_item_slot_item_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		buyerUserId: text('buyer_user_id').notNull(),
		slotId: uuid('slot_id'),
		orderItemId: uuid('order_item_id').notNull(),
		entityType: entityType('entity_type').notNull(),
		entityId: uuid('entity_id').notNull(),
		registrationTypeId: uuid('registration_type_id').notNull(),
		unitPrice: numeric('unit_price', { precision: 10, scale: 2 }).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`),
		removedAt: timestamp('removed_at', { mode: 'string' }),
		removedBy: text('removed_by'),
		removedReason: varchar('removed_reason', { length: 50 })
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'order_item_slot_items_event_id_fk'
		}),
		foreignKey({
			columns: [table.buyerUserId],
			foreignColumns: [users.id],
			name: 'order_item_slot_items_buyer_user_id_fk'
		}),
		foreignKey({
			columns: [table.slotId],
			foreignColumns: [orderItemSlots.orderItemSlotId],
			name: 'order_item_slot_items_slot_id_fk'
		}),
		foreignKey({
			columns: [table.orderItemId],
			foreignColumns: [orderItems.orderItemId],
			name: 'order_item_slot_items_order_item_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'order_item_slot_items_registration_type_id_fk'
		}),
		uniqueIndex('order_item_slot_items_one_ticket_per_slot')
			.on(table.slotId)
			.where(sql`entity_type = 'ticket' AND removed_at IS NULL AND slot_id IS NOT NULL`),
		index('order_item_slot_items_event_buyer_idx').on(table.eventId, table.buyerUserId),
		index('order_item_slot_items_slot_idx').on(table.slotId),
		index('order_item_slot_items_order_item_idx').on(table.orderItemId),
		index('order_item_slot_items_entity_idx').on(table.entityType, table.entityId)
	]
);

export const refundRules = pgTable(
	'refund_rules',
	{
		refundRuleId: uuid('refund_rule_id').defaultRandom().primaryKey().notNull(),
		refundPolicyId: uuid('refund_policy_id').notNull(),
		startDate: timestamp('start_date', { mode: 'string' }).notNull(),
		endDate: timestamp('end_date', { mode: 'string' }).notNull(),
		refundType: varchar('refund_type', { length: 50 }).notNull(),
		refundValue: numeric('refund_value', { precision: 10, scale: 2 }).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' })
			.default(sql`CURRENT_TIMESTAMP`)
			.notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
			.default(sql`CURRENT_TIMESTAMP`)
			.notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.refundPolicyId],
			foreignColumns: [refundPolicies.refundPolicyId],
			name: 'refund_rules_refund_policy_id_fkey'
		}).onDelete('cascade'),
		check(
			'refund_rules_refund_type_check',
			sql`(refund_type)::text = ANY (ARRAY[('percentage'::character varying)::text, ('flat'::character varying)::text])`
		),
		check('refund_rules_refund_value_check', sql`refund_value >= (0)::numeric`)
	]
);

export const registrationTypes = pgTable(
	'registration_types',
	{
		registrationTypeId: uuid('registration_type_id').defaultRandom().primaryKey().notNull(),
		name: varchar({ length: 50 }).notNull(),
		organizationId: text('organization_id').notNull(),
		description: text(),
		isShareable: boolean('is_shareable').default(false).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		reportingCode: varchar('reporting_code', { length: 255 }),
		eventId: uuid('event_id'),
		createdBy: text('created_by'),
		updatedBy: text('updated_by'),
		deletedBy: text('deleted_by'),
		tenantLevelId: uuid('tenant_level_id'),
		isHidden: boolean('is_hidden').default(false).notNull(),
		isDefault: boolean('is_default').default(false).notNull(),
		uniqueCode: text('unique_code').generatedAlwaysAs(
			sql`upper(encode(SUBSTRING(decode(md5((registration_type_id)::text), 'hex'::text) FROM 1 FOR 4), 'hex'::text))`
		)
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'registration_type_tenant_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'registration_types_events_event_id_fk'
		}),
		uniqueIndex('registration_types_unique_code_uindex').using(
			'btree',
			table.uniqueCode.asc().nullsLast().op('text_ops')
		)
	]
);

export const addons = pgTable(
	'addons',
	{
		addonId: uuid('addon_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id'),
		addonTypeId: uuid('addon_type_id'),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		details: json(),
		price: numeric({ precision: 10, scale: 2 }).notNull(),
		isDefault: boolean('is_default').default(false).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		totalQuantity: integer('total_quantity'),
		maxQuantityPerOrder: integer('max_quantity_per_order').default(1),
		availabilityStart: timestamp('availability_start', { mode: 'string' }),
		availabilityEnd: timestamp('availability_end', { mode: 'string' }),
		salesCloseMode: salesCloseMode('sales_close_mode').default('inherit').notNull(),
		refundPolicyId: uuid('refund_policy_id'),
		isPublished: boolean('is_published').default(true),
		displayOrder: integer('display_order').default(0)
	},
	(table) => [
		foreignKey({
			columns: [table.addonTypeId],
			foreignColumns: [addonTypes.addonTypeId],
			name: 'addons_addon_type_id_addon_types_addon_id_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'addons_event_id'
		}),
		foreignKey({
			columns: [table.refundPolicyId],
			foreignColumns: [refundPolicies.refundPolicyId],
			name: 'addons_refund_policies_refund_policy_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'addons_organization_id'
		})
	]
);

export const addonTypes = pgTable(
	'addon_types',
	{
		addonTypeId: uuid('addon_type_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		details: json(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'addon_types_tenants'
		})
	]
);

export const sessionPackages = pgTable(
	'session_packages',
	{
		sessionPackageId: uuid('session_package_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		name: varchar({ length: 255 }).notNull(),
		description: text(),
		price: numeric({ precision: 10, scale: 2 }).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 }),
		totalQuantity: integer('total_quantity'),
		availabilityStart: timestamp('availability_start', { mode: 'string' }),
		availabilityEnd: timestamp('availability_end', { mode: 'string' }),
		refundPolicyId: uuid('refund_policy_id'),
		isPublished: boolean('is_published').default(true)
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'session_packages_event_id_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.refundPolicyId],
			foreignColumns: [refundPolicies.refundPolicyId],
			name: 'session_packages_refund_policies_refund_policy_id_fk'
		})
	]
);

export const registrationMessages = pgTable(
	'registration_messages',
	{
		registrationMessageId: uuid('registration_message_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id'),
		eventId: uuid('event_id'),
		messageType: varchar('message_type', { length: 100 }).default('COMPLETION'),
		messageContent: text('message_content').notNull(),
		name: text().notNull(),
		description: text(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		isDefault: boolean('is_default').default(false).notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'registration_messages_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'registration_messages_organization_id_fk'
		}).onDelete('cascade')
	]
);

export const registrationMessageAssociations = pgTable(
	'registration_message_associations',
	{
		registrationMessagesAssociationId: uuid('registration_messages_association_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		registrationMessageId: uuid('registration_message_id'),
		registrationTypeId: uuid('registration_type_id'),
		ticketId: uuid('ticket_id'),
		createAt: timestamp('create_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		index('registration_message_associations_key_index').using(
			'btree',
			table.registrationMessageId.asc().nullsLast().op('uuid_ops'),
			table.registrationTypeId.asc().nullsLast().op('uuid_ops'),
			table.ticketId.asc().nullsLast().op('uuid_ops')
		),
		foreignKey({
			columns: [table.registrationMessageId],
			foreignColumns: [registrationMessages.registrationMessageId],
			name: 'registration_message_associations_registration_message_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'registration_message_associations_registration_type_id_fk'
		}),
		foreignKey({
			columns: [table.ticketId],
			foreignColumns: [tickets.ticketId],
			name: 'registration_message_associations_tickets_ticket_id_fk'
		})
	]
);

export const registrationTypeOverrides = pgTable(
	'registration_type_overrides',
	{
		registrationTypeOverrideId: uuid('registration_type_override_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		eventId: uuid('event_id').notNull(),
		registrationTypeId: uuid('registration_type_id').notNull(),
		entityId: uuid('entity_id').notNull(),
		entityType: varchar('entity_type', { length: 50 }).notNull(),
		price: numeric({ precision: 10, scale: 2 }),
		startDate: timestamp('start_date', { mode: 'string' }),
		endDate: timestamp('end_date', { mode: 'string' }),
		overrideDetails: json('override_details'),
		isPublished: boolean('is_published').default(true).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		refundPolicyId: uuid('refund_policy_id')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'registration_type_overrides_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.refundPolicyId],
			foreignColumns: [refundPolicies.refundPolicyId],
			name: 'registration_type_overrides_refund_policies_refund_policy_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'registration_type_overrides_registration_type_fk'
		})
	]
);

export const sessionContentItems = pgTable(
	'session_content_items',
	{
		agendaSessionId: uuid('agenda_session_id').notNull(),
		contentId: uuid('content_id').notNull(),
		section: varchar({ length: 100 }),
		ordinal: integer().default(99999).notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.contentId],
			foreignColumns: [contentItems.contentItemId],
			name: 'session_content_item_content_fk'
		}),
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'session_content_item_session_fk'
		}),
		primaryKey({
			columns: [table.agendaSessionId, table.contentId],
			name: 'session_content_items_session_id_content_id_pk'
		})
	]
);

export const defaultEventSettings = pgTable(
	'default_event_settings',
	{
		settingKey: text('setting_key').notNull(),
		settingValue: jsonb('setting_value').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`),
		updatedAt: timestamp('updated_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`),
		organizationId: text('organization_id')
			.default(sql`'00000000-0000-0000-0000-000000000000'`)
			.notNull()
	},
	(table) => [
		index('default_event_settings_organization_id_setting_key_index').using(
			'btree',
			table.settingKey.asc().nullsLast().op('text_ops'),
			table.organizationId.asc().nullsLast().op('text_ops')
		),
		primaryKey({ columns: [table.settingKey, table.organizationId], name: 'default_event_settings_pk' })
	]
);

export const sessionsMedia = pgTable(
	'sessions_media',
	{
		agendaSessionId: uuid('agenda_session_id').notNull(),
		mediaId: uuid('media_id').notNull(),
		ordinal: integer().default(99999).notNull(),
		isHero: boolean('is_hero').default(false),
		isThumbnail: boolean('is_thumbnail').default(false),
		isDownloadable: boolean('is_downloadable').default(false)
	},
	(table) => [
		foreignKey({
			columns: [table.mediaId],
			foreignColumns: [media.mediaId],
			name: 'sessions_media_media_id_media_media_id_fk'
		}),
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'sessions_media_session_id_sessions_session_id_fk'
		}),
		primaryKey({ columns: [table.agendaSessionId, table.mediaId], name: 'sessions_media_session_id_media_id_pk' })
	]
);

export const eventSettings = pgTable(
	'event_settings',
	{
		eventId: uuid('event_id').notNull(),
		settingKey: text('setting_key').notNull(),
		settingValue: jsonb('setting_value').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`),
		updatedBy: text('updated_by')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'events_settings_event_id_events_event_id_fk'
		}),
		primaryKey({ columns: [table.eventId, table.settingKey], name: 'event_settings_pkey' })
	]
);

export const sessionPackagesSessions = pgTable(
	'session_packages_sessions',
	{
		sessionPackageId: uuid('session_package_id').notNull(),
		agendaSessionId: uuid('agenda_session_id').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'session_packages_sessions_session_fk'
		}),
		foreignKey({
			columns: [table.sessionPackageId],
			foreignColumns: [sessionPackages.sessionPackageId],
			name: 'session_packages_sessions_session_package_fk'
		}),
		primaryKey({
			columns: [table.sessionPackageId, table.agendaSessionId],
			name: 'session_packages_sessions_session_package_id_session_id_pk'
		})
	]
);

export const registrationTypeItems = pgTable(
	'registration_type_items',
	{
		eventId: uuid('event_id').notNull(),
		registrationTypeId: uuid('registration_type_id').notNull(),
		entityId: uuid('entity_id').notNull(),
		entityType: varchar('entity_type', { length: 50 }).notNull(),
		isPublished: boolean('is_published').default(true).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'registration_type_items_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'registration_type_items_registration_type_fk'
		}),
		primaryKey({
			columns: [table.eventId, table.registrationTypeId, table.entityId, table.entityType],
			name: 'registration_type_items_pk'
		})
	]
);

export const registrationItemAssociations = pgTable(
	'registration_item_associations',
	{
		registrationItemAssociationId: uuid('registration_item_association_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		registrationTypeId: uuid('registration_type_id').notNull(),
		ticketId: uuid('ticket_id'),
		entityId: uuid('entity_id').notNull(),
		entityType: varchar('entity_type', { length: 50 }).notNull(),
		isPublished: boolean('is_published').default(true).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by')
	},
	(table) => [
		index('registration_item_associations_key_index').using(
			'btree',
			table.eventId.asc().nullsLast().op('uuid_ops'),
			table.entityId.asc().nullsLast().op('uuid_ops'),
			table.registrationTypeId.asc().nullsLast().op('uuid_ops'),
			table.ticketId.asc().nullsLast().op('uuid_ops')
		),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'registration_item_associations_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'registration_item_associations_registration_type_id_fk'
		}),
		foreignKey({
			columns: [table.ticketId],
			foreignColumns: [tickets.ticketId],
			name: 'registration_item_associations_tickets_ticket_id_fk'
		})
	]
);

export const discounts = pgTable(
	'discounts',
	{
		discountId: uuid('discount_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id'),
		displayName: text('display_name').notNull(),
		description: text(),
		codeBehavior: varchar('code_behavior', { length: 30 }).default('discount').notNull(),
		discountType: varchar('discount_type', { length: 20 }),
		discountValue: numeric('discount_value', { precision: 10, scale: 4 }),
		currency: varchar({ length: 3 }).default('usd').notNull(),
		maxTotalUses: integer('max_total_uses'),
		maxUsesPerEmail: integer('max_uses_per_email'),
		maxUsesPerUser: integer('max_uses_per_user'),
		maxQuantityPerOrder: integer('max_quantity_per_order'),
		minEligibleAmount: numeric('min_eligible_amount', { precision: 10, scale: 2 }),
		validFrom: timestamp('valid_from', { mode: 'string' }),
		validTo: timestamp('valid_to', { mode: 'string' }),
		stackable: boolean().default(false).notNull(),
		usageCount: integer('usage_count').default(0).notNull(),
		isPublished: boolean('is_published').default(true).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'discounts_organization_id_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'discounts_event_id_fk'
		}).onDelete('cascade'),
		index('discounts_organization_id_idx').on(table.organizationId),
		index('discounts_event_id_idx').on(table.eventId)
	]
);

export const discountCodes = pgTable(
	'discount_codes',
	{
		discountCodeId: uuid('discount_code_id').defaultRandom().primaryKey().notNull(),
		discountId: uuid('discount_id').notNull(),
		code: varchar({ length: 64 }).notNull(),
		codeNormalized: text('code_normalized').generatedAlwaysAs(sql`upper(code)`),
		maxTotalUses: integer('max_total_uses'),
		usageCount: integer('usage_count').default(0).notNull(),
		isActive: boolean('is_active').default(true).notNull(),
		generatedInBatchId: uuid('generated_in_batch_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.discountId],
			foreignColumns: [discounts.discountId],
			name: 'discount_codes_discount_id_fk'
		}).onDelete('cascade'),
		index('discount_codes_discount_id_idx').on(table.discountId),
		index('discount_codes_code_normalized_idx').on(table.codeNormalized)
	]
);

export const discountCodeRedemptions = pgTable(
	'discount_code_redemptions',
	{
		discountCodeRedemptionId: uuid('discount_code_redemption_id').defaultRandom().primaryKey().notNull(),
		discountId: uuid('discount_id').notNull(),
		discountCodeId: uuid('discount_code_id').notNull(),
		orderId: uuid('order_id').notNull(),
		orderItemId: uuid('order_item_id'),
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id').notNull(),
		userId: text('user_id'),
		email: varchar({ length: 255 }).notNull(),
		discountAmount: numeric('discount_amount', { precision: 10, scale: 2 }).notNull(),
		currency: varchar({ length: 3 }).notNull(),
		unitsConsumed: integer('units_consumed').default(1).notNull(),
		redeemedAt: timestamp('redeemed_at', { mode: 'string' }).defaultNow().notNull(),
		confirmedAt: timestamp('confirmed_at', { mode: 'string' }),
		voidedAt: timestamp('voided_at', { mode: 'string' })
	},
	(table) => [
		foreignKey({
			columns: [table.discountId],
			foreignColumns: [discounts.discountId],
			name: 'discount_code_redemptions_discount_id_fk'
		}),
		foreignKey({
			columns: [table.discountCodeId],
			foreignColumns: [discountCodes.discountCodeId],
			name: 'discount_code_redemptions_discount_code_id_fk'
		}),
		foreignKey({
			columns: [table.orderId],
			foreignColumns: [orders.orderId],
			name: 'discount_code_redemptions_order_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.attendeeRegistrationStatusId],
			foreignColumns: [attendeeRegistrationStatuses.attendeeRegistrationStatusId],
			name: 'discount_code_redemptions_status_id_fk'
		})
	]
);

export const discountApplicability = pgTable(
	'discount_applicability',
	{
		discountId: uuid('discount_id').notNull(),
		entityType: varchar('entity_type', { length: 50 }).notNull(),
		entityId: uuid('entity_id').notNull(),
		mode: varchar({ length: 10 }).default('include').notNull()
	},
	(table) => [
		primaryKey({ columns: [table.discountId, table.entityType, table.entityId] }),
		foreignKey({
			columns: [table.discountId],
			foreignColumns: [discounts.discountId],
			name: 'discount_applicability_discount_id_fk'
		}).onDelete('cascade')
	]
);

export const eventDiscounts = pgTable(
	'event_discounts',
	{
		eventId: uuid('event_id').notNull(),
		discountId: uuid('discount_id').notNull(),
		publishedAt: timestamp('published_at', { mode: 'string' }).defaultNow().notNull(),
		publishedBy: text('published_by')
	},
	(table) => [
		primaryKey({ columns: [table.eventId, table.discountId] }),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'event_discounts_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.discountId],
			foreignColumns: [discounts.discountId],
			name: 'event_discounts_discount_id_fk'
		}).onDelete('cascade')
	]
);

export const sessionTags = pgTable(
	'session_tags',
	{
		agendaSessionId: uuid('agenda_session_id').notNull(),
		tagId: uuid('tag_id').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'session_tag_session_fk'
		}),
		foreignKey({
			columns: [table.tagId],
			foreignColumns: [tags.tagId],
			name: 'session_tag_tag_fk'
		}),
		primaryKey({ columns: [table.agendaSessionId, table.tagId], name: 'session_tags_session_id_tag_id_pk' })
	]
);

export const sessionRegistrations = pgTable(
	'session_registrations',
	{
		agendaSessionId: uuid('agenda_session_id').notNull(),
		attendeeId: uuid('attendee_id').notNull(),
		hasAttended: boolean('has_attended').default(false).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'session_registration_session_fk'
		}),
		primaryKey({
			columns: [table.agendaSessionId, table.attendeeId],
			name: 'session_registrations_session_id_attendee_id_pk'
		})
	]
);

export const sessionWaitlist = pgTable(
	'session_waitlist',
	{
		agendaSessionId: uuid('agenda_session_id').notNull(),
		attendeeId: uuid('attendee_id').notNull(),
		sessionRegistrationId: uuid('session_registration_id'),
		invitedToRegister: boolean('invited_to_register').default(false).notNull(),
		registrationExpires: timestamp('registration_expires', { mode: 'string' }),
		isMovedToRegistration: boolean('is_moved_to_registration').default(false).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by'),
		reportingCode: varchar('reporting_code', { length: 255 })
	},
	(table) => [
		foreignKey({
			columns: [table.attendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'session_waitlist_attendee_fk'
		}),
		foreignKey({
			columns: [table.agendaSessionId],
			foreignColumns: [agendaSessions.agendaSessionId],
			name: 'session_waitlist_session_fk'
		}),
		primaryKey({
			columns: [table.agendaSessionId, table.attendeeId],
			name: 'session_waitlist_session_id_attendee_id_pk'
		})
	]
);

export const emailTemplates = pgTable(
	'email_templates',
	{
		emailTemplateId: uuid('email_template_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id'),
		eventId: uuid('event_id'),
		usageKey: varchar('usage_key', { length: 100 }).notNull(),
		name: text().notNull(),
		subject: text().notNull(),
		body: text().notNull(),
		config: jsonb('config').$type<{
			button?: { text?: string; href?: string };
			buttons?: Array<{ text: string; href: string }>;
			headerImage?: string;
			hideOrderSummaryForFree?: boolean;
			hideCheckInQr?: boolean;
			hideCalendarButton?: boolean;
			hideManageRegistrationLink?: boolean;
		}>(),
		fromName: text('from_name'),
		replyToEmail: text('reply_to_email'),
		// Set when this event template was copied in via "Import from Org"; points at
		// the org-level template it originated from so the list can badge it. Nulls out
		// if that origin template is later deleted.
		sourceOrgTemplateId: uuid('source_org_template_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: integer('updated_by'),
		isDefault: boolean('is_default').default(false).notNull(),
		isPaused: boolean('is_paused').default(false).notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'email_templates_event_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'email_templates_organization_id_fk'
		}),
		foreignKey({
			columns: [table.sourceOrgTemplateId],
			foreignColumns: [table.emailTemplateId],
			name: 'email_templates_source_org_template_id_fk'
		}).onDelete('set null'),
		uniqueIndex('email_templates_event_default_usage_key_uniq')
			.on(table.eventId, table.usageKey)
			.where(sql`is_default = true AND event_id IS NOT NULL`),
		uniqueIndex('email_templates_org_default_usage_key_uniq')
			.on(table.organizationId, table.usageKey)
			.where(sql`is_default = true AND event_id IS NULL`)
	]
);
export const emailTemplateAssociations = pgTable(
	'email_template_associations',
	{
		emailTemplateAssociationId: uuid('email_template_association_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		emailTemplateId: uuid('email_template_id'),
		registrationTypeId: uuid('registration_type_id'),
		ticketId: uuid('ticket_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		index('email_template_associations_key_index').using(
			'btree',
			table.emailTemplateId.asc().nullsLast().op('uuid_ops'),
			table.registrationTypeId.asc().nullsLast().op('uuid_ops'),
			table.ticketId.asc().nullsLast().op('uuid_ops')
		),
		foreignKey({
			columns: [table.emailTemplateId],
			foreignColumns: [emailTemplates.emailTemplateId],
			name: 'email_template_associations_registration_message_id_fk'
		}),
		foreignKey({
			columns: [table.registrationTypeId],
			foreignColumns: [registrationTypes.registrationTypeId],
			name: 'email_template_associations_registration_type_id_fk'
		}),
		foreignKey({
			columns: [table.ticketId],
			foreignColumns: [tickets.ticketId],
			name: 'email_template_associations_tickets_ticket_id_fk'
		})
	]
);

export const emailEventLogs = pgTable(
	'email_event_logs',
	{
		emailEventLogId: uuid('email_event_log_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		eventId: uuid('event_id'),
		emailTemplateId: uuid('email_template_id'),
		from: text('from').notNull(),
		subject: text('subject').notNull(),
		recipientEmail: text('recipient_email').notNull(),
		recipientAttendeeId: uuid('recipient_attendee_id'),
		recipientUserId: text('recipient_user_id'),
		orderId: uuid('order_id'),
		triggeredBy: text('triggered_by').notNull(),
		isTestSend: boolean('is_test_send').default(false).notNull(),
		eventType: varchar('event_type', { length: 100 }).notNull(),
		emailId: text('email_id'),
		data: jsonb('data'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: integer('created_by'),
		campaignId: uuid('campaign_id')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'email_event_log_event_id_fk'
		}),
		foreignKey({
			columns: [table.emailTemplateId],
			foreignColumns: [emailTemplates.emailTemplateId],
			name: 'email_event_log_email_template_id_fk'
		}),
		index('email_event_logs_campaign_idx').on(table.campaignId)
	]
);

export const suppressionReason = pgEnum('suppression_reason', ['hard_bounce', 'complaint', 'manual']);

export const contactListSource = pgEnum('contact_list_source', ['attendee', 'invite']);
export const contactListKind = pgEnum('contact_list_kind', ['saved', 'adhoc']);
export const contactListType = pgEnum('contact_list_type', ['dynamic', 'manual', 'hybrid', 'synced']);
export const contactListMemberMode = pgEnum('contact_list_member_mode', ['include', 'exclude']);
export const contactListPurpose = pgEnum('contact_list_purpose', ['broadcast', 'invite']);
export const campaignScheduleType = pgEnum('campaign_schedule_type', ['now', 'fixed', 'relative']);
export const campaignRelativeAnchor = pgEnum('campaign_relative_anchor', ['event_start', 'event_end']);
export const campaignStatus = pgEnum('campaign_status', [
	'draft',
	'scheduled',
	'sending',
	'sent',
	'sent_with_errors',
	'canceled',
	'failed'
]);
export const campaignRecipientStatus = pgEnum('campaign_recipient_status', [
	'pending',
	'sending',
	'sent',
	'skipped',
	'failed'
]);

export const unsubscribeScope = pgEnum('unsubscribe_scope', ['org', 'event']);

export const unsubscribeSource = pgEnum('unsubscribe_source', ['one_click', 'manual']);

export const unsubscribeStream = pgEnum('unsubscribe_stream', ['broadcast', 'marketing']);

export const emailSuppressions = pgTable(
	'email_suppressions',
	{
		emailSuppressionId: uuid('email_suppression_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		email: text('email').notNull(),
		reason: suppressionReason('reason').notNull(),
		organizationId: text('organization_id'),
		eventId: uuid('event_id'),
		resendEmailId: text('resend_email_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
	},
	(table) => [uniqueIndex('email_suppressions_email_unique').on(table.email)]
);

export const emailUnsubscribes = pgTable(
	'email_unsubscribes',
	{
		emailUnsubscribeId: uuid('email_unsubscribe_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		email: text('email').notNull(),
		scope: unsubscribeScope('scope').notNull(),
		organizationId: text('organization_id'),
		eventId: uuid('event_id'),
		stream: unsubscribeStream('stream').notNull(),
		source: unsubscribeSource('source').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow()
	},
	(table) => [
		unique('email_unsubscribes_scope_unique')
			.on(table.email, table.scope, table.organizationId, table.eventId, table.stream)
			.nullsNotDistinct()
	]
);

export const emailVariableDefinitions = pgTable(
	'email_variable_definitions',
	{
		emailVariableDefinitionId: uuid('email_variable_definition_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id'),
		eventId: uuid('event_id'),
		usageKey: varchar('usage_key', { length: 100 }).notNull(),
		key: varchar('key', { length: 100 }).notNull(),
		display: text('display').notNull(),
		allowedFields: text('allowed_fields').array(),
		valueSource: varchar('value_source', { length: 20 }).notNull().default('computed'),
		valuePath: text('value_path'),
		valueTransform: varchar('value_transform', { length: 50 }),
		displayOrder: integer('display_order').default(0).notNull(),
		isActive: boolean('is_active').default(true).notNull(),
		sampleValue: text('sample_value'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'email_variable_definitions_event_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'email_variable_definitions_organization_id_fk'
		}),
		unique('email_variable_definitions_scope_unique')
			.on(table.organizationId, table.eventId, table.usageKey, table.key)
			.nullsNotDistinct(),
		index('idx_email_var_defs_usage_key').using('btree', table.usageKey),
		index('idx_email_var_defs_event_id').using('btree', table.eventId)
	]
);

export const featureFlags = pgTable(
	'feature_flags',
	{
		featureFlagId: uuid('feature_flag_id').defaultRandom().primaryKey().notNull(),
		name: varchar({ length: 100 }).notNull(),
		description: text(),
		isDefaultEnabled: boolean('is_default_enabled').default(false).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by')
	},
	(table) => [
		unique('feature_flags_name_key').on(table.name),
		unique('feature_flags_feature_flag_id_key').on(table.featureFlagId)
	]
);

export const organizationFeatureFlags = pgTable(
	'organization_feature_flags',
	{
		organizationFeatureFlagId: uuid('organization_feature_flag_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		featureFlagId: uuid('feature_flag_id').notNull(),
		isEnabled: boolean('is_enabled').default(false).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by')
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'organization_feature_flags_organization_id_fk'
		}),
		foreignKey({
			columns: [table.featureFlagId],
			foreignColumns: [featureFlags.featureFlagId],
			name: 'organization_feature_flags_feature_flag_id_fk'
		}),
		unique('organization_feature_flags_organization_id_feature_flag_id_key').on(
			table.organizationId,
			table.featureFlagId
		)
	]
);

export const paymentProviderEnum = pgEnum('payment_provider', ['stripe', 'square', 'paypal']);

export const organizationPaymentConfig = pgTable(
	'organization_payment_config',
	{
		organizationId: text('organization_id').primaryKey().notNull(),

		// Payment provider
		provider: paymentProviderEnum().default('stripe').notNull(),
		providerAccountId: varchar('provider_account_id', { length: 255 }),
		providerOnboardingComplete: boolean('provider_onboarding_complete').default(false).notNull(),
		testMode: boolean('test_mode').default(false).notNull(),

		// Provider-specific configuration (varies by provider)
		providerConfig: jsonb('provider_config').$type<{
			// Stripe-specific
			stripeAccountType?: 'express' | 'standard' | 'custom';
			stripeCapabilities?: string[];
			// Square-specific
			squareLocationId?: string;
			// PayPal-specific
			paypalMerchantId?: string;
			// Generic fields for future providers
			[key: string]: unknown;
		}>(),

		// Sunfish fee configuration (platform-level, applies to all providers)
		sunfishFeeEnabled: boolean('sunfish_fee_enabled').default(true).notNull(),
		sunfishFeePercent: numeric('sunfish_fee_percent', { precision: 5, scale: 4 }).default('0.01').notNull(),
		sunfishFeeFixed: numeric('sunfish_fee_fixed', { precision: 10, scale: 2 }).default('0.99').notNull(),
		sunfishFeeCap: numeric('sunfish_fee_cap', { precision: 10, scale: 2 }).default('4.99').notNull(),

		// Allowed payment methods (superset for all events under this org)
		// null = automatic (Stripe decides). Array = explicit list e.g. ['card', 'cashapp', 'us_bank_account']
		allowedPaymentMethods: jsonb('allowed_payment_methods').$type<string[] | null>(),

		// Currency
		defaultCurrency: varchar('default_currency', { length: 3 }).default('usd').notNull(),

		// Audit
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by')
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'organization_payment_config_organization_id_fk'
		})
	]
);

export const adminLinks = pgTable(
	'admin_links',
	{
		adminLinkId: uuid('admin_link_id').defaultRandom().primaryKey().notNull(),
		name: varchar({ length: 255 }).notNull(),
		url: text('url').notNull(),
		icon: text('icon'),
		ordinal: integer('ordinal').default(9999).notNull(),
		usage: varchar({ length: 100 }),
		parentLinkId: uuid('parent_link_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by')
	},
	(table) => [
		foreignKey({
			columns: [table.parentLinkId],
			foreignColumns: [table.adminLinkId],
			name: 'admin_links_parent_link_id_fk'
		})
	]
);

export const adminLinkConditions = pgTable(
	'admin_link_conditions',
	{
		adminLinkConditionId: uuid('admin_link_condition_id').defaultRandom().primaryKey().notNull(),
		adminLinkId: uuid('admin_link_id').notNull(),
		conditionType: varchar('condition_type', { length: 50 }).notNull(),
		conditionField: varchar('condition_field', { length: 100 }).notNull(),
		conditionOperation: varchar('condition_operation', { length: 50 }).notNull(),
		conditionValue: text('condition_value'),
		combination: text('combination').default('AND').notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.adminLinkId],
			foreignColumns: [adminLinks.adminLinkId],
			name: 'admin_link_conditions_admin_link_id_fk'
		})
	]
);

// Landing Pages Schema
export const landingPages = pgTable(
	'landing_pages',
	{
		landingPageId: uuid('landing_page_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		title: text().notNull(),
		urlSlug: varchar('url_slug', { length: 255 }).notNull(),
		settings: jsonb().$type<{
			heroCardSettings?: {
				showEventName?: boolean;
				showEventType?: boolean;
				showDateAndTime?: boolean;
				showVenue?: boolean;
				showAddress?: boolean;
				showRegisterButton?: boolean;
				registerButtonText?: string;
				// 'fill' crops the hero image to cover the section, 'contain' shows all
				// of it. Absent means 'fill', which is how every page rendered before.
				heroImageFit?: 'fill' | 'contain';
				// How heavily the hero darkens the artwork behind its text. Absent
				// means the 'medium' default; see @sunfish/lib/landing-page/hero-tint.
				heroTint?: 'subtle' | 'medium' | 'strong';
			};
			headerSettings?: {
				showLogo?: boolean;
				showEventType?: boolean;
				showLoginLink?: boolean;
				loginLinkText?: string;
				showRegisterButton?: boolean;
				registerButtonText?: string;
				// Organizer-managed jump links to blocks on the same page. `target` is
				// a block anchor name (see @sunfish/lib/landing-page/block-anchors),
				// stored without the '#'.
				navLinks?: { label: string; target: string }[];
			};
			footerSettings?: {
				socialLinks?: Array<{
					key: 'x' | 'instagram' | 'linkedin' | 'youtube' | 'bluesky' | 'threads' | 'website' | 'tiktok' | 'email';
					label: string;
					url: string;
				}>;
				terms?: {
					mode: 'text' | 'link';
					label?: string;
					text?: string;
					url?: string;
				};
				showPoweredBy?: boolean;
			};
		}>(),
		publishedAt: timestamp('published_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'landing_pages_event_id_fk'
		}).onDelete('cascade'),
		unique('landing_pages_url_slug_key').on(table.urlSlug),
		index('landing_pages_event_id_idx').using('btree', table.eventId.asc().nullsLast()),
		index('landing_pages_published_at_idx').using('btree', table.publishedAt.asc().nullsLast())
	]
);

export const landingPagePreviewTokens = pgTable(
	'landing_page_preview_tokens',
	{
		tokenId: uuid('token_id').defaultRandom().primaryKey().notNull(),
		landingPageId: uuid('landing_page_id').notNull(),
		token: varchar('token', { length: 64 }).notNull().unique(),
		expiresAt: timestamp('expires_at', { mode: 'string' }).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		createdBy: text('created_by'),
		usedAt: timestamp('used_at', { mode: 'string' })
	},
	(table) => [
		foreignKey({
			columns: [table.landingPageId],
			foreignColumns: [landingPages.landingPageId],
			name: 'landing_page_preview_tokens_landing_page_id_fk'
		}).onDelete('cascade'),
		index('landing_page_preview_tokens_token_idx').using('btree', table.token.asc()),
		index('landing_page_preview_tokens_expires_at_idx').using('btree', table.expiresAt.asc())
	]
);

export const landingPageBlocks = pgTable(
	'landing_page_blocks',
	{
		landingPageBlockId: uuid('landing_page_block_id').defaultRandom().primaryKey().notNull(),
		landingPageId: uuid('landing_page_id').notNull(),
		blockType: varchar('block_type', { length: 100 }).notNull(),
		content: jsonb().$type<{
			name?: string;
			description?: string;
			imageUrl?: string;
			buttonText?: string;
			buttonUrl?: string;
			showRegistrationButton?: boolean;
			showLoginToReviewButton?: boolean;
			[key: string]: any;
		}>(),
		displayOrder: integer('display_order').default(0).notNull(),
		isVisible: boolean('is_visible').default(true).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.landingPageId],
			foreignColumns: [landingPages.landingPageId],
			name: 'landing_page_blocks_landing_page_id_fk'
		}).onDelete('cascade'),
		index('landing_page_blocks_landing_page_id_idx').using('btree', table.landingPageId.asc().nullsLast()),
		index('landing_page_blocks_display_order_idx').using('btree', table.displayOrder.asc().nullsLast())
	]
);

// Analytics Data Cache
export const analyticsData = pgTable(
	'analytics_data',
	{
		analyticsDataId: uuid('analytics_data_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id'),
		landingPageId: uuid('landing_page_id'),

		// Cache identification
		cacheKey: text('cache_key').notNull().unique(),
		queryType: varchar('query_type', { length: 100 }).notNull(), // 'pageviews', 'visitors', 'timeseries', etc.

		// Cached data
		data: jsonb().notNull(),

		// Expiration
		expiresAt: timestamp('expires_at', { mode: 'string' }).notNull(),

		// Audit
		createdAt: timestamp('created_at', { withTimezone: true, mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { withTimezone: true, mode: 'string' })
			.defaultNow()
			.notNull()
			.$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'analytics_data_organization_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'analytics_data_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.landingPageId],
			foreignColumns: [landingPages.landingPageId],
			name: 'analytics_data_landing_page_id_fk'
		}).onDelete('cascade'),
		index('analytics_data_cache_key_idx').using('btree', table.cacheKey.asc().nullsLast()),
		index('analytics_data_expires_at_idx').using('btree', table.expiresAt.asc().nullsLast()),
		index('analytics_data_org_event_landing_idx').using(
			'btree',
			table.organizationId.asc().nullsLast(),
			table.eventId.asc().nullsLast(),
			table.landingPageId.asc().nullsLast()
		)
	]
);

// Export Batches
export const exportBatches = pgTable(
	'export_batches',
	{
		exportBatchId: uuid('export_batch_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id').notNull(),
		userId: text('user_id').notNull(),

		// Batch metadata
		name: text('name'), // Optional user-friendly name like "Weekly Attendee Export"
		description: text('description'),

		// Status tracking
		status: exportBatchStatus().default('pending').notNull(),
		totalJobs: integer('total_jobs').default(0).notNull(),
		completedJobs: integer('completed_jobs').default(0).notNull(),
		failedJobs: integer('failed_jobs').default(0).notNull(),

		// Error handling
		errorMessage: text('error_message'),

		// Audit
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		startedAt: timestamp('started_at', { mode: 'string' }),
		completedAt: timestamp('completed_at', { mode: 'string' }),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'export_batches_organization_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'export_batches_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.userId],
			foreignColumns: [users.id],
			name: 'export_batches_user_id_fk'
		}).onDelete('cascade'),
		index('export_batches_organization_id_idx').using('btree', table.organizationId.asc().nullsLast()),
		index('export_batches_event_id_idx').using('btree', table.eventId.asc().nullsLast()),
		index('export_batches_user_id_idx').using('btree', table.userId.asc().nullsLast()),
		index('export_batches_status_idx').using('btree', table.status.asc().nullsLast()),
		index('export_batches_created_at_idx').using('btree', table.createdAt.desc().nullsLast())
	]
);

// Export Jobs
export const exportJobs = pgTable(
	'export_jobs',
	{
		exportJobId: uuid('export_job_id').defaultRandom().primaryKey().notNull(),
		exportBatchId: uuid('export_batch_id').notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id').notNull(),
		userId: text('user_id').notNull(),

		// Job type and configuration
		type: exportJobType().notNull(),
		config: jsonb().notNull(), // { filters, selectedColumns } - schema varies by type

		// Execution order: jobs with same ordinal run in parallel, lower ordinals run first
		ordinal: integer('ordinal').default(0).notNull(),

		// Status tracking
		status: exportJobStatus().default('pending').notNull(),

		// Results
		fileName: text('file_name'),
		fileSize: integer('file_size'), // Size in bytes
		recordCount: integer('record_count'), // Number of records exported
		downloadUrl: text('download_url'),
		expiresAt: timestamp('expires_at', { mode: 'string' }),

		// Error handling
		errorMessage: text('error_message'),
		retryCount: integer('retry_count').default(0).notNull(),

		// Audit
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		startedAt: timestamp('started_at', { mode: 'string' }),
		completedAt: timestamp('completed_at', { mode: 'string' }),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.exportBatchId],
			foreignColumns: [exportBatches.exportBatchId],
			name: 'export_jobs_batch_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'export_jobs_organization_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'export_jobs_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.userId],
			foreignColumns: [users.id],
			name: 'export_jobs_user_id_fk'
		}).onDelete('cascade'),
		index('export_jobs_batch_id_idx').using('btree', table.exportBatchId.asc().nullsLast()),
		index('export_jobs_organization_id_idx').using('btree', table.organizationId.asc().nullsLast()),
		index('export_jobs_event_id_idx').using('btree', table.eventId.asc().nullsLast()),
		index('export_jobs_user_id_idx').using('btree', table.userId.asc().nullsLast()),
		index('export_jobs_status_idx').using('btree', table.status.asc().nullsLast()),
		index('export_jobs_type_idx').using('btree', table.type.asc().nullsLast()),
		index('export_jobs_created_at_idx').using('btree', table.createdAt.desc().nullsLast()),
		// Composite index for finding next jobs to execute (batch + ordinal + status)
		index('export_jobs_batch_ordinal_status_idx').using(
			'btree',
			table.exportBatchId.asc().nullsLast(),
			table.ordinal.asc().nullsLast(),
			table.status.asc().nullsLast()
		)
	]
);

// Import Enums
export const importBatchStatus = pgEnum('import_batch_status', [
	'pending',
	'validating',
	'validated',
	'importing',
	'completed',
	'failed'
]);

export const importRowStatus = pgEnum('import_row_status', [
	'pending',
	'valid',
	'invalid',
	'skipped',
	'imported',
	'failed'
]);

export const importType = pgEnum('import_type', ['completed_waived', 'completed_payment_required', 'pending']);

export const importEntityType = pgEnum('import_entity_type', ['attendee', 'session', 'speaker', 'sponsor']);

// Import Batches
export const importBatches = pgTable(
	'import_batches',
	{
		importBatchId: uuid('import_batch_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id').notNull(),
		userId: text('user_id').notNull(),

		// File metadata
		originalFileName: text('original_file_name').notNull(),
		rowCount: integer('row_count').default(0).notNull(),
		validRowCount: integer('valid_row_count').default(0).notNull(),
		skippedRowCount: integer('skipped_row_count').default(0).notNull(),
		invalidRowCount: integer('invalid_row_count').default(0).notNull(),
		importedRowCount: integer('imported_row_count').default(0).notNull(),

		// Status tracking
		status: importBatchStatus().default('pending').notNull(),
		entityType: importEntityType('entity_type').default('attendee').notNull(),

		// Import configuration
		importType: importType('import_type'),
		columnMapping: jsonb('column_mapping'),
		sendConfirmationEmail: boolean('send_confirmation_email').default(false).notNull(),
		sendPaymentLinkEmail: boolean('send_payment_link_email').default(false).notNull(),
		sendInviteEmail: boolean('send_invite_email').default(false).notNull(),

		// Error handling
		errorMessage: text('error_message'),

		// Audit
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		validatedAt: timestamp('validated_at', { mode: 'string' }),
		completedAt: timestamp('completed_at', { mode: 'string' }),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'import_batches_organization_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'import_batches_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.userId],
			foreignColumns: [users.id],
			name: 'import_batches_user_id_fk'
		}).onDelete('cascade'),
		index('import_batches_organization_id_idx').using('btree', table.organizationId.asc().nullsLast()),
		index('import_batches_event_id_idx').using('btree', table.eventId.asc().nullsLast()),
		index('import_batches_user_id_idx').using('btree', table.userId.asc().nullsLast()),
		index('import_batches_status_idx').using('btree', table.status.asc().nullsLast()),
		index('import_batches_entity_type_idx').using('btree', table.entityType.asc().nullsLast()),
		index('import_batches_created_at_idx').using('btree', table.createdAt.desc().nullsLast())
	]
);

// Import Rows (staging table for CSV rows)
export const importRows = pgTable(
	'import_rows',
	{
		importRowId: uuid('import_row_id').defaultRandom().primaryKey().notNull(),
		importBatchId: uuid('import_batch_id').notNull(),
		rowNumber: integer('row_number').notNull(),

		// Data
		rawData: jsonb('raw_data').notNull(),
		parsedData: jsonb('parsed_data'),

		// PR 2: per-row resolution payload (operation, matched attendee, diff,
		// errors, warnings). Populated by validate-batch. Consumed by
		// execute-import and the preview UI.
		resolution: jsonb('resolution'),

		// Status
		status: importRowStatus().default('pending').notNull(),
		validationErrors: jsonb('validation_errors'),
		isSelected: boolean('is_selected').default(true).notNull(),
		isDuplicate: boolean('is_duplicate').default(false).notNull(),

		// Attendee imports keep writing attendeeId, which carries a real FK that
		// existing queries depend on. Agenda imports write entityId, which is
		// polymorphic across agenda_sessions/speakers/sponsors and so cannot have
		// one. A later migration can backfill attendeeId into entityId and drop it.
		attendeeId: uuid('attendee_id'),
		entityId: uuid('entity_id'),

		// Audit
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.importBatchId],
			foreignColumns: [importBatches.importBatchId],
			name: 'import_rows_batch_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.attendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'import_rows_attendee_id_fk'
		}).onDelete('set null'),
		index('import_rows_batch_id_idx').using('btree', table.importBatchId.asc().nullsLast()),
		index('import_rows_status_idx').using('btree', table.status.asc().nullsLast()),
		index('import_rows_row_number_idx').using('btree', table.rowNumber.asc().nullsLast()),
		index('import_rows_batch_row_idx').using(
			'btree',
			table.importBatchId.asc().nullsLast(),
			table.rowNumber.asc().nullsLast()
		)
	]
);

export const stripeTestCalls = pgTable(
	'stripe_test_calls',
	{
		id: uuid('id').defaultRandom().primaryKey().notNull(),
		runId: uuid('run_id').notNull(),
		orderId: uuid('order_id'),
		method: text('method').notNull(),
		request: jsonb('request').notNull(),
		response: jsonb('response').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [index('stripe_test_calls_run_id_idx').on(table.runId, table.createdAt)]
);

export const smokeTestPreviews = pgTable(
	'smoke_test_previews',
	{
		id: uuid('id').defaultRandom().primaryKey().notNull(),
		runId: uuid('run_id').notNull(),
		orderId: uuid('order_id'),
		attendeeId: uuid('attendee_id'),
		stepIndex: integer('step_index').notNull(),
		proposedChanges: jsonb('proposed_changes').notNull(),
		previewResult: jsonb('preview_result').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [index('smoke_test_previews_run_id_idx').on(table.runId, table.createdAt)]
);

export const allocationReservations = pgTable(
	'allocation_reservations',
	{
		reservationId: uuid('reservation_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id').notNull(),
		attendeeRegistrationStatusId: uuid('attendee_registration_status_id').notNull(),
		entityType: varchar('entity_type', { length: 32 }).notNull(),
		entityId: uuid('entity_id').notNull(),
		quantity: integer('quantity').default(1).notNull(),
		expiresAt: timestamp('expires_at', { mode: 'string', withTimezone: true }).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' })
			.default(sql`CURRENT_TIMESTAMP`)
			.notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
			.default(sql`CURRENT_TIMESTAMP`)
			.notNull()
			.$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'allocation_reservations_organization_id_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'allocation_reservations_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.attendeeRegistrationStatusId],
			foreignColumns: [attendeeRegistrationStatuses.attendeeRegistrationStatusId],
			name: 'allocation_reservations_status_id_fk'
		}).onDelete('cascade'),
		unique('allocation_reservations_status_entity_unique').on(
			table.attendeeRegistrationStatusId,
			table.entityType,
			table.entityId
		),
		index('allocation_reservations_entity_active_idx').using(
			'btree',
			table.entityType.asc(),
			table.entityId.asc(),
			table.expiresAt.asc()
		),
		index('allocation_reservations_expires_at_idx').using('btree', table.expiresAt.asc())
	]
);

export const checkInPoints = pgTable(
	'check_in_points',
	{
		checkInPointId: uuid('check_in_point_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		name: varchar({ length: 255 }).notNull(),
		type: checkInPointType('type').default('event').notNull(),
		sortOrder: integer('sort_order').default(0).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'check_in_points_event_id_fk'
		}).onDelete('cascade'),
		index('check_in_points_event_id_idx').on(table.eventId)
	]
);

export const checkInDevices = pgTable(
	'check_in_devices',
	{
		deviceId: uuid('device_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		name: varchar({ length: 255 }).notNull(),
		mode: checkInDeviceMode('mode').default('operator').notNull(),
		provisionedByUserId: text('provisioned_by_user_id'),
		lastSeenAt: timestamp('last_seen_at', { mode: 'string' }),
		revokedAt: timestamp('revoked_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'check_in_devices_event_id_fk'
		}).onDelete('cascade'),
		index('check_in_devices_event_id_idx').on(table.eventId)
	]
);

// A pairing profile (check_in_devices row) can have many physical devices,
// each holding its own bearer token, so one can be revoked or re-paired
// without logging out its siblings.
export const checkInDeviceTokens = pgTable(
	'check_in_device_tokens',
	{
		tokenId: uuid('token_id').defaultRandom().primaryKey().notNull(),
		deviceId: uuid('device_id').notNull(),
		eventId: uuid('event_id').notNull(),
		tokenHash: text('token_hash').notNull(),
		lastSeenAt: timestamp('last_seen_at', { mode: 'string' }),
		revokedAt: timestamp('revoked_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		foreignKey({
			columns: [table.deviceId],
			foreignColumns: [checkInDevices.deviceId],
			name: 'check_in_device_tokens_device_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'check_in_device_tokens_event_id_fk'
		}).onDelete('cascade'),
		uniqueIndex('check_in_device_tokens_token_hash_idx').on(table.tokenHash),
		index('check_in_device_tokens_device_id_idx').on(table.deviceId)
	]
);

export const checkIns = pgTable(
	'check_ins',
	{
		checkInId: uuid('check_in_id').defaultRandom().primaryKey().notNull(),
		clientId: uuid('client_id').notNull(),
		attendeeId: uuid('attendee_id').notNull(),
		eventId: uuid('event_id').notNull(),
		checkInPointId: uuid('check_in_point_id').notNull(),
		deviceId: uuid('device_id'),
		direction: checkInDirection('direction').notNull(),
		source: checkInSource('source').notNull(),
		voidsClientId: uuid('voids_client_id'),
		claimedName: text('claimed_name'),
		claimedEmail: text('claimed_email'),
		occurredAt: timestamp('occurred_at', { mode: 'string' }).notNull(),
		recordedByUserId: text('recorded_by_user_id'),
		syncedAt: timestamp('synced_at', { mode: 'string' }).defaultNow(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'check_ins_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.attendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'check_ins_attendee_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.checkInPointId],
			foreignColumns: [checkInPoints.checkInPointId],
			name: 'check_ins_check_in_point_id_fk'
		}),
		foreignKey({
			columns: [table.deviceId],
			foreignColumns: [checkInDevices.deviceId],
			name: 'check_ins_device_id_fk'
		}),
		uniqueIndex('check_ins_client_id_uindex').on(table.clientId),
		index('check_ins_event_attendee_idx').on(table.eventId, table.attendeeId),
		index('check_ins_event_point_idx').on(table.eventId, table.checkInPointId)
	]
);

export const checkInNotes = pgTable(
	'check_in_notes',
	{
		noteId: uuid('note_id').defaultRandom().primaryKey().notNull(),
		attendeeId: uuid('attendee_id').notNull(),
		eventId: uuid('event_id').notNull(),
		body: text('body').notNull(),
		deviceId: uuid('device_id'),
		createdByUserId: text('created_by_user_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'check_in_notes_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.attendeeId],
			foreignColumns: [attendees.attendeeId],
			name: 'check_in_notes_attendee_id_fk'
		}).onDelete('cascade'),
		index('check_in_notes_event_attendee_idx').on(table.eventId, table.attendeeId)
	]
);

export const eventSigningKeys = pgTable(
	'event_signing_keys',
	{
		eventSigningKeyId: uuid('event_signing_key_id').defaultRandom().primaryKey().notNull(),
		eventId: uuid('event_id').notNull(),
		publicKey: text('public_key').notNull(),
		privateKey: text('private_key').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow()
	},
	(table) => [
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'event_signing_keys_event_id_fk'
		}).onDelete('cascade'),
		uniqueIndex('event_signing_keys_event_id_uindex').on(table.eventId)
	]
);

export const contacts = pgTable(
	'contacts',
	{
		contactId: uuid('contact_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		email: text('email').notNull(),
		firstName: text('first_name'),
		lastName: text('last_name'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' }),
		externalMemberId: text('external_member_id'),
		membershipLevel: text('membership_level'),
		membershipStatus: text('membership_status'),
		joinedAt: timestamp('joined_at', { mode: 'string' }),
		renewsAt: timestamp('renews_at', { mode: 'string' }),
		attributes: jsonb('attributes').default({}),
		syncedFromIntegrationId: uuid('synced_from_integration_id')
	},
	(table) => [
		unique('contacts_org_email_unique').on(table.organizationId, table.email),
		index('contacts_org_idx').on(table.organizationId),
		index('contacts_org_membership_status_idx').on(table.organizationId, table.membershipStatus),
		// Emails are matched case-insensitively (sync chunks, audience joins); the
		// unique constraint above is on the raw column and cannot serve lower().
		index('contacts_org_lower_email_idx').on(table.organizationId, sql`lower(${table.email})`),
		foreignKey({
			columns: [table.syncedFromIntegrationId],
			foreignColumns: [integrations.integrationId],
			name: 'contacts_synced_from_integration_id_fk'
		}).onDelete('set null')
	]
);

export const contactLists = pgTable(
	'contact_lists',
	{
		contactListId: uuid('contact_list_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id'),
		name: text('name').notNull(),
		source: contactListSource('source').notNull().default('attendee'),
		kind: contactListKind('kind').notNull().default('saved'),
		type: contactListType('type').notNull().default('dynamic'),
		purpose: contactListPurpose('purpose').notNull().default('broadcast'),
		criteria: jsonb('criteria'),
		systemKey: text('system_key'),
		importedFromContactListId: uuid('imported_from_contact_list_id'),
		createdBy: text('created_by'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
	},
	(table) => [
		uniqueIndex('contact_lists_org_system_key_uq')
			.on(table.organizationId, table.systemKey)
			.where(sql`${table.eventId} is null and ${table.systemKey} is not null`),
		uniqueIndex('contact_lists_event_system_key_uq')
			.on(table.eventId, table.systemKey)
			.where(sql`${table.eventId} is not null and ${table.systemKey} is not null`)
	]
);

export const contactListMembers = pgTable(
	'contact_list_members',
	{
		contactListMemberId: uuid('contact_list_member_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		contactListId: uuid('contact_list_id').notNull(),
		mode: contactListMemberMode('mode').notNull(),
		attendeeId: uuid('attendee_id'),
		contactId: uuid('contact_id').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow()
	},
	(table) => [unique('contact_list_members_unique').on(table.contactListId, table.mode, table.contactId)]
);

export const contactListDefaultValues = pgTable(
	'contact_list_default_values',
	{
		contactListDefaultValueId: uuid('contact_list_default_value_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		contactListId: uuid('contact_list_id').notNull(),
		formFieldId: uuid('form_field_id').notNull(),
		value: jsonb(),
		formFieldOptionId: uuid('form_field_option_id'),
		createdBy: text('created_by'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
	},
	(table) => [
		unique('contact_list_default_values_unique').on(table.contactListId, table.formFieldId),
		foreignKey({
			columns: [table.contactListId],
			foreignColumns: [contactLists.contactListId],
			name: 'contact_list_default_values_list_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.formFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'contact_list_default_values_field_fk'
		}),
		foreignKey({
			columns: [table.formFieldOptionId],
			foreignColumns: [formFieldOptions.formFieldOptionId],
			name: 'contact_list_default_values_option_fk'
		}).onDelete('cascade')
	]
);

export const contactListMemberValues = pgTable(
	'contact_list_member_values',
	{
		contactListMemberValueId: uuid('contact_list_member_value_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		contactListMemberId: uuid('contact_list_member_id').notNull(),
		formFieldId: uuid('form_field_id').notNull(),
		value: jsonb(),
		formFieldOptionId: uuid('form_field_option_id'),
		// Null for a hand-edited value; set when a sync run owns it.
		writtenBySyncRunId: uuid('written_by_sync_run_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
	},
	(table) => [
		unique('contact_list_member_values_unique').on(table.contactListMemberId, table.formFieldId),
		foreignKey({
			columns: [table.contactListMemberId],
			foreignColumns: [contactListMembers.contactListMemberId],
			name: 'contact_list_member_values_member_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.formFieldId],
			foreignColumns: [formFields.formFieldId],
			name: 'contact_list_member_values_field_fk'
		}),
		foreignKey({
			columns: [table.formFieldOptionId],
			foreignColumns: [formFieldOptions.formFieldOptionId],
			name: 'contact_list_member_values_option_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.writtenBySyncRunId],
			foreignColumns: [integrationSyncRuns.integrationSyncRunId],
			name: 'contact_list_member_values_run_fk'
		}).onDelete('set null')
	]
);

export const eventInviteStatus = pgEnum('event_invite_status', ['pending', 'invited', 'registered', 'revoked']);
export const eventInviteRequestStatus = pgEnum('event_invite_request_status', ['pending', 'promoted', 'dismissed']);

export const eventInvites = pgTable(
	'event_invites',
	{
		eventInviteId: uuid('event_invite_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		eventId: uuid('event_id').notNull(),
		email: text('email').notNull(),
		firstName: text('first_name'),
		lastName: text('last_name'),
		token: text('token').notNull(),
		status: eventInviteStatus('status').notNull().default('pending'),
		registrationTypeId: uuid('registration_type_id'),
		sourceContactListId: uuid('source_contact_list_id'),
		attendeeId: uuid('attendee_id'),
		contactId: uuid('contact_id'),
		invitedAt: timestamp('invited_at', { mode: 'string' }),
		registeredAt: timestamp('registered_at', { mode: 'string' }),
		// Set when a membership sync revoked this invite, so the sync only ever
		// restores what it took away and never an organiser's own revocation.
		revokedBySyncConfigId: uuid('revoked_by_sync_config_id'),
		// Set when a membership sync chose registration_type_id. The sync only
		// rewrites a group it set itself; picking one by hand clears this.
		registrationTypeSetBySyncConfigId: uuid('registration_type_set_by_sync_config_id'),
		createdBy: text('created_by'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
	},
	(table) => [
		unique('event_invites_event_email_unique').on(table.eventId, table.email),
		unique('event_invites_token_unique').on(table.token),
		index('event_invites_event_idx').on(table.eventId),
		foreignKey({
			columns: [table.revokedBySyncConfigId],
			foreignColumns: [integrationSyncConfigs.integrationSyncConfigId],
			name: 'event_invites_revoked_by_sync_config_fk'
		}).onDelete('set null'),
		foreignKey({
			columns: [table.registrationTypeSetBySyncConfigId],
			foreignColumns: [integrationSyncConfigs.integrationSyncConfigId],
			name: 'event_invites_group_set_by_sync_config_fk'
		}).onDelete('set null')
	]
);

export const eventInviteLists = pgTable(
	'event_invite_lists',
	{
		eventInviteListId: uuid('event_invite_list_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		eventId: uuid('event_id').notNull(),
		contactListId: uuid('contact_list_id').notNull(),
		defaultRegistrationTypeId: uuid('default_registration_type_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow()
	},
	(table) => [unique('event_invite_lists_unique').on(table.eventId, table.contactListId)]
);

export const eventInviteRequests = pgTable(
	'event_invite_requests',
	{
		eventInviteRequestId: uuid('event_invite_request_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		eventId: uuid('event_id').notNull(),
		email: text('email').notNull(),
		firstName: text('first_name'),
		lastName: text('last_name'),
		status: eventInviteRequestStatus('status').notNull().default('pending'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
	},
	(table) => [unique('event_invite_requests_event_email_unique').on(table.eventId, table.email)]
);

export const campaigns = pgTable(
	'campaigns',
	{
		campaignId: uuid('campaign_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id').notNull(),
		contactListId: uuid('contact_list_id').notNull(),
		emailTemplateId: uuid('email_template_id').notNull(),
		segment: jsonb('segment'),
		name: text('name').notNull(),
		fromName: text('from_name'),
		replyToEmail: text('reply_to_email'),
		scheduleType: campaignScheduleType('schedule_type').notNull().default('now'),
		scheduledFor: timestamp('scheduled_for', { mode: 'string' }),
		relativeOffsetMinutes: integer('relative_offset_minutes'),
		relativeAnchor: campaignRelativeAnchor('relative_anchor'),
		status: campaignStatus('status').notNull().default('draft'),
		recipientCountSnapshot: integer('recipient_count_snapshot'),
		remainingBatches: integer('remaining_batches'),
		sentAt: timestamp('sent_at', { mode: 'string' }),
		failedReason: text('failed_reason'),
		createdBy: text('created_by'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		index('campaigns_due_idx')
			.on(table.scheduledFor)
			.where(sql`status = 'scheduled'`)
	]
);

export const campaignRecipients = pgTable(
	'campaign_recipients',
	{
		campaignRecipientId: uuid('campaign_recipient_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		campaignId: uuid('campaign_id').notNull(),
		email: text('email').notNull(),
		attendeeId: uuid('attendee_id'),
		contactId: uuid('contact_id'),
		eventInviteId: uuid('event_invite_id'),
		status: campaignRecipientStatus('status').notNull().default('pending'),
		skipReason: text('skip_reason'),
		errorReason: text('error_reason'),
		claimedAt: timestamp('claimed_at', { mode: 'string' }),
		sentAt: timestamp('sent_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow()
	},
	(table) => [unique('campaign_recipients_unique').on(table.campaignId, table.email)]
);

export const organizationSettings = pgTable(
	'organization_settings',
	{
		organizationSettingId: uuid('organization_setting_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		mailingStreet: text('mailing_street'),
		mailingCity: text('mailing_city'),
		mailingState: text('mailing_state'),
		mailingPostalCode: text('mailing_postal_code'),
		mailingCountry: text('mailing_country'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
	},
	(table) => [uniqueIndex('organization_settings_org_unique').on(table.organizationId)]
);

export const userMilestones = pgTable(
	'user_milestones',
	{
		userId: text('user_id').notNull(),
		milestoneKey: text('milestone_key').notNull(),
		seenAt: timestamp('seen_at', { mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull()
	},
	(table) => [
		primaryKey({ columns: [table.userId, table.milestoneKey], name: 'user_milestones_pkey' }),
		foreignKey({
			columns: [table.userId],
			foreignColumns: [users.id],
			name: 'user_milestones_user_id_users_id_fk'
		}).onDelete('cascade')
	]
);

export const eventGoals = pgTable(
	'event_goals',
	{
		eventGoalId: uuid('event_goal_id').defaultRandom().primaryKey().notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id').notNull(),
		// One of EVENT_GOAL_METRICS in @sunfish/lib. Left as varchar rather than a
		// pg enum because @sunfish/db cannot import @sunfish/lib (lib depends on
		// db), and an enum would need a migration every time a metric is added.
		// Readers narrow with isEventGoalMetric and drop anything unrecognised.
		metric: varchar('metric', { length: 50 }).notNull(),
		// Whole units of whatever the metric counts — people, tickets, add-ons,
		// check-ins, or dollars. Net revenue is stored and compared in dollars, the
		// same unit computeRevenueWaterfall returns.
		target: integer().notNull(),
		// The goal's deadline. Null means "inherit the event's end date" — resolve
		// with resolveEventGoalDeadline in @sunfish/lib, never by reading this
		// column directly. Snapshotting the event's end date here instead would
		// silently strand every goal on an old deadline when an event moves.
		deadlineAt: timestamp('deadline_at', { mode: 'string' }),
		// Written by the goal notification cron, which does not exist yet. Null on
		// every row today and null is not evidence a goal was never reached.
		achievedAt: timestamp('achieved_at', { mode: 'string' }),
		notifiedAt: timestamp('notified_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow(),
		createdBy: text('created_by'),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`),
		updatedBy: text('updated_by'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedBy: text('deleted_by')
	},
	(table) => [
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'event_goals_organization_id_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'event_goals_event_id_fk'
		}).onDelete('cascade'),
		uniqueIndex('event_goals_event_metric_uq')
			.on(table.eventId, table.metric)
			.where(sql`deleted_at IS NULL`),
		index('event_goals_event_id_idx').on(table.eventId)
	]
);

export const integrationNotificationEvents = pgTable(
	'integration_notification_events',
	{
		integrationEventId: uuid('integration_event_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id'),
		type: varchar('type', { length: 100 }).notNull(),
		apiVersion: varchar('api_version', { length: 20 }).notNull(),
		payload: jsonb('payload').notNull(),
		// Set when an event must fire at most once for a real-world occurrence (a
		// user's first login, an org completing Stripe onboarding). The partial
		// unique index below is what enforces it, which is why no emitting call site
		// needs a lock or a read-before-write to check whether it already fired.
		dedupeKey: text('dedupe_key'),
		// Set on an event addressed to one rule (a digest). The dispatcher delivers it
		// to that rule only and never matches it against any other.
		subscriptionId: uuid('subscription_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		publishedAt: timestamp('published_at', { mode: 'string' })
	},
	(table) => [
		// The relay's hot path is "everything not yet published, oldest first". A
		// partial index keeps it the size of the backlog rather than the archive.
		index('integration_notification_events_unpublished_idx')
			.on(table.createdAt)
			.where(sql`published_at IS NULL`),
		index('integration_notification_events_org_type_idx').on(table.organizationId, table.type),
		uniqueIndex('integration_notification_events_dedupe_key_uindex')
			.on(table.dedupeKey)
			.where(sql`dedupe_key IS NOT NULL`),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'integration_notification_events_organization_id_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'integration_notification_events_event_id_fk'
		}),
		foreignKey({
			columns: [table.subscriptionId],
			foreignColumns: [integrationNotificationSubscriptions.integrationSubscriptionId],
			name: 'integration_notification_events_subscription_id_fk'
		}).onDelete('cascade')
	]
);

export const integrationConnections = pgTable(
	'integration_connections',
	{
		integrationConnectionId: uuid('integration_connection_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		// Null means Sunfish owns this connection rather than a customer. Only
		// internalAdminProcedure can write a null.
		organizationId: text('organization_id'),
		provider: varchar('provider', { length: 64 }).notNull(),
		externalAccountRef: varchar('external_account_ref', { length: 255 }),
		displayName: varchar('display_name', { length: 255 }),
		credentialsEnc: text('credentials_enc'),
		config: jsonb('config').default({}).notNull(),
		syncState: jsonb('sync_state').default({}).notNull(),
		status: integrationConnectionStatus('status').default('active').notNull(),
		errorReason: text('error_reason'),
		connectedByUserId: text('connected_by_user_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		// Two partial indexes rather than one on (org, provider, ref): Postgres treats
		// NULLs as distinct in a unique index, so a system-owned connection (null org)
		// would never conflict with itself and Phase 3's onConflictDoUpdate would
		// silently insert duplicates instead of updating. NULLS NOT DISTINCT would
		// also work but needs PG15+, and partial indexes need no version assumption.
		uniqueIndex('integration_connections_org_unique')
			.on(table.organizationId, table.provider, table.externalAccountRef)
			.where(sql`organization_id IS NOT NULL`),
		uniqueIndex('integration_connections_system_unique')
			.on(table.provider, table.externalAccountRef)
			.where(sql`organization_id IS NULL`),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'integration_connections_organization_id_fk'
		})
	]
);

export const integrationNotificationSubscriptions = pgTable(
	'integration_notification_subscriptions',
	{
		integrationSubscriptionId: uuid('integration_subscription_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		// Null means "every organization's events". Only internalAdminProcedure can
		// write a null; an org-scoped caller can never produce one.
		organizationId: text('organization_id'),
		provider: varchar('provider', { length: 64 }).notNull(),
		integrationConnectionId: uuid('integration_connection_id'),
		eventTypes: text('event_types').array().notNull(),
		filter: jsonb('filter').default({}).notNull(),
		target: jsonb('target').default({}).notNull(),
		cadence: varchar('cadence', { length: 16 }).default('immediate').notNull(),
		// Null for an immediate rule. Which stats and columns each section shows,
		// and the timezone the daily and weekly boundaries fall in.
		digestConfig: jsonb('digest_config'),
		// The next window starts here. Reset to now when a rule is re-enabled or its
		// cadence changes, so a rule switched off for a month does not send the
		// month as one digest.
		digestLastWindowEnd: timestamp('digest_last_window_end', { mode: 'string' }),
		secretEnc: text('secret_enc'),
		status: integrationSubscriptionStatus('status').default('enabled').notNull(),
		disabledReason: text('disabled_reason'),
		// Zapier REST Hooks create subscriptions through the partner API rather than
		// the UI, and revoking an API key must disable the hooks it created.
		source: varchar('source', { length: 20 }).default('user').notNull(),
		createdByApiKeyId: uuid('created_by_api_key_id'),
		consecutiveFailures: integer('consecutive_failures').default(0).notNull(),
		// Reserved for the sustained-transient-failure auto-disable in a later phase.
		// Nothing writes it yet; a null here is not evidence a subscription never failed.
		firstFailureAt: timestamp('first_failure_at', { mode: 'string' }),
		createdByUserId: text('created_by_user_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		index('integration_notification_subscriptions_dispatch_idx').on(table.status, table.provider),
		index('integration_notification_subscriptions_digest_due_idx')
			.on(table.digestLastWindowEnd)
			.where(sql`cadence <> 'immediate' AND status = 'enabled'`),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'integration_notification_subscriptions_organization_id_fk'
		}),
		foreignKey({
			columns: [table.integrationConnectionId],
			foreignColumns: [integrationConnections.integrationConnectionId],
			name: 'integration_notification_subscriptions_connection_id_fk'
		}).onDelete('cascade')
	]
);

export const integrationNotificationDeliveries = pgTable(
	'integration_notification_deliveries',
	{
		integrationDeliveryId: uuid('integration_delivery_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		integrationEventId: uuid('integration_event_id').notNull(),
		integrationSubscriptionId: uuid('integration_subscription_id').notNull(),
		status: integrationDeliveryStatus('status').default('pending').notNull(),
		attemptCount: integer('attempt_count').default(0).notNull(),
		nextAttemptAt: timestamp('next_attempt_at', { mode: 'string' }),
		lastAttemptAt: timestamp('last_attempt_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		// The fan-out idempotency seam. An at-least-once relay can replay an event
		// freely because the second insert collapses into this constraint.
		uniqueIndex('integration_notification_deliveries_event_subscription_unique').on(
			table.integrationEventId,
			table.integrationSubscriptionId
		),
		index('integration_notification_deliveries_due_idx')
			.on(table.nextAttemptAt)
			.where(sql`status IN ('pending', 'failed')`),
		foreignKey({
			columns: [table.integrationEventId],
			foreignColumns: [integrationNotificationEvents.integrationEventId],
			name: 'integration_notification_deliveries_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.integrationSubscriptionId],
			foreignColumns: [integrationNotificationSubscriptions.integrationSubscriptionId],
			name: 'integration_notification_deliveries_subscription_id_fk'
		}).onDelete('cascade')
	]
);

export const integrationNotificationDeliveryAttempts = pgTable(
	'integration_notification_delivery_attempts',
	{
		integrationDeliveryAttemptId: uuid('integration_delivery_attempt_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		integrationDeliveryId: uuid('integration_delivery_id').notNull(),
		attemptNumber: integer('attempt_number').notNull(),
		responseStatus: integer('response_status'),
		responseBodySnippet: text('response_body_snippet'),
		durationMs: integer('duration_ms'),
		errorClass: varchar('error_class', { length: 50 }),
		attemptedAt: timestamp('attempted_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [
		index('integration_notification_delivery_attempts_delivery_idx').on(table.integrationDeliveryId),
		index('integration_notification_delivery_attempts_retention_idx').on(table.attemptedAt),
		foreignKey({
			columns: [table.integrationDeliveryId],
			foreignColumns: [integrationNotificationDeliveries.integrationDeliveryId],
			name: 'integration_notification_delivery_attempts_delivery_id_fk'
		}).onDelete('cascade')
	]
);

export const integrationNotificationMessageTemplates = pgTable(
	'integration_notification_message_templates',
	{
		integrationMessageTemplateId: uuid('integration_message_template_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		// Null means the system-scoped wording, used by rules Sunfish itself owns.
		// Scopes do not inherit from each other: an organization with no row here
		// falls back to the template that ships in code, never to the system row.
		organizationId: text('organization_id'),
		// One of INTEGRATION_EVENTS in @sunfish/lib. Varchar rather than a pg enum
		// for the same reason as event_goals.metric: @sunfish/db cannot import
		// @sunfish/lib, and an enum would need a migration per event type.
		eventType: varchar('event_type', { length: 100 }).notNull(),
		// A MessageTemplate from @sunfish/lib. Stored whole rather than as a diff so
		// the saved row is exactly what gets sent; resolveMessageTemplate still
		// merges it over the code default so a row missing a key cannot render
		// undefined. Deleting the row is what "reset to default" means.
		template: jsonb('template').notNull(),
		updatedByUserId: text('updated_by_user_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		// Two partial indexes for the same reason as integration_connections:
		// Postgres treats NULLs as distinct, so one combined index would let a
		// system-scoped template be inserted twice instead of upserting.
		uniqueIndex('integration_notification_message_templates_org_unique')
			.on(table.organizationId, table.eventType)
			.where(sql`organization_id IS NOT NULL`),
		uniqueIndex('integration_notification_message_templates_system_unique')
			.on(table.eventType)
			.where(sql`organization_id IS NULL`),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'integration_notification_message_templates_organization_id_fk'
		})
	]
);

export const integrationRecordEvents = pgTable(
	'integration_record_events',
	{
		integrationRecordEventId: uuid('integration_record_event_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		eventId: uuid('event_id'),
		resource: varchar('resource', { length: 50 }).notNull(),
		type: varchar('type', { length: 100 }).notNull(),
		recordId: text('record_id').notNull(),
		apiVersion: varchar('api_version', { length: 20 }).notNull(),
		payload: jsonb('payload').notNull(),
		// One id threads an inbound write through its event to every delivery it
		// causes, so a full round trip is a single query in the trace sink.
		correlationId: uuid('correlation_id').notNull(),
		// Set when this event was caused by a write from a connection. Fan-out skips
		// that connection's own subscriptions, which is half the echo guard.
		originConnectionId: uuid('origin_connection_id'),
		// The same guard for connectionless providers. A customer wiring their CRM
		// both ways holds one API key and an endpoint that belongs to it; neither
		// has a connection, so origin_connection_id stays null on both sides and
		// never matches. Marking the key that caused the write lets fan-out skip
		// the endpoints that key owns, which is what stops a two-way sync echoing.
		originApiKeyId: uuid('origin_api_key_id'),
		// Set when an event must fire at most once for a real-world occurrence - an
		// order completing, say, which several code paths can reach. The partial
		// unique index below enforces it, so no emitting call site needs a lock or a
		// read-before-write to decide whether it already fired.
		dedupeKey: text('dedupe_key'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		publishedAt: timestamp('published_at', { mode: 'string' })
	},
	(table) => [
		uniqueIndex('integration_record_events_dedupe_key_uindex')
			.on(table.dedupeKey)
			.where(sql`dedupe_key IS NOT NULL`),
		index('integration_record_events_unpublished_idx')
			.on(table.createdAt)
			.where(sql`published_at IS NULL`),
		index('integration_record_events_org_type_idx').on(table.organizationId, table.type),
		index('integration_record_events_correlation_idx').on(table.correlationId),
		// Supports the no-op guard: "what did we last publish for this record?".
		// Without it that lookup scans the outbox on every emit.
		index('integration_record_events_record_idx').on(
			table.resource,
			table.recordId,
			table.createdAt
		),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'integration_record_events_organization_id_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'integration_record_events_event_id_fk'
		})
	]
);

// Shared by api_keys, oauth_grants and oauth_clients: the same two-value scale
// for "is this credential for real data or for trying things out".
export const apiKeyEnvironment = pgEnum('api_key_environment', ['live', 'test']);

export const oauthClients = pgTable(
	'oauth_clients',
	{
		oauthClientId: uuid('oauth_client_id').default(sql`gen_random_uuid()`).primaryKey().notNull(),
		clientKey: varchar('client_key', { length: 64 }).notNull(),
		// The client secret itself is never stored: it is returned once at
		// registration and is unrecoverable afterwards.
		secretHash: text('secret_hash').notNull(),
		name: varchar('name', { length: 120 }).notNull(),
		description: text('description'),
		// The app's own mark, so a customer recognises it in a list the way they
		// recognise it everywhere else. A URL rather than an upload because the
		// registry is internal-only and the asset belongs to the partner, not to
		// us. Null falls back to a generic icon; anything not https is ignored.
		logoUrl: text('logo_url'),
		// What an organization with no row of its own gets. True keeps an app on
		// the shelf for everybody, which is what every app registered before this
		// column existed was. False makes it opt-in, for a partner in pilot or one
		// whose contract is per-customer.
		defaultAvailable: boolean('default_available').default(true).notNull(),
		// Chosen at registration so a test app and a live one can coexist in one
		// database. Defaults to live: every client registered before this column
		// existed was one, because the grant path hardcoded it.
		environment: apiKeyEnvironment('environment').default('live').notNull(),
		redirectUris: text('redirect_uris').array().notNull(),
		allowedScopes: text('allowed_scopes').array().notNull(),
		disabledAt: timestamp('disabled_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [uniqueIndex('oauth_clients_client_key_unique').on(table.clientKey)]
);

export const integrationDirection = pgEnum('integration_direction', ['out', 'in', 'both', 'pull']);
export const integrationMechanism = pgEnum('integration_mechanism', ['self_serve', 'partner_app']);
export const integrationStatus = pgEnum('integration_status', ['active', 'disabled']);

// Direction records who initiates, not which way bytes travel: a read-only key
// that polls /v1 moves data out of Sunfish but is plainly `in`, because they
// call us. `pull` is the third case — we call them on a schedule and the data
// comes to us. Entitlement stays on the org feature flags; this row records what
// one integration does inside that ceiling.
export const integrations = pgTable(
	'integrations',
	{
		integrationId: uuid('integration_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		name: varchar('name', { length: 120 }).notNull(),
		direction: integrationDirection('direction').notNull(),
		mechanism: integrationMechanism('mechanism').notNull(),
		// Unconstrained until the partner app catalog exists.
		partnerAppId: uuid('partner_app_id'),
		status: integrationStatus('status').default('active').notNull(),
		disabledReason: text('disabled_reason'),
		createdByUserId: text('created_by_user_id'),
		// Soft, because the delivery log hangs off this row by a cascading foreign
		// key: a hard delete takes the history with it, and the history is usually
		// why somebody is removing the integration in the first place.
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedByUserId: text('deleted_by_user_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		index('integrations_organization_idx').on(table.organizationId, table.status),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'integrations_organization_id_fk'
		}),
		foreignKey({
			columns: [table.partnerAppId],
			foreignColumns: [oauthClients.oauthClientId],
			name: 'integrations_partner_app_id_fk'
		})
	]
);

export const integrationRecordSubscriptions = pgTable(
	'integration_record_subscriptions',
	{
		integrationRecordSubscriptionId: uuid('integration_record_subscription_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id'),
		provider: varchar('provider', { length: 64 }).notNull(),
		integrationConnectionId: uuid('integration_connection_id'),
		// What the customer called this integration. Nullable because endpoints
		// created before the setup flow existed have no name, and a URL is a poor
		// substitute for one when the same integration also owns a key.
		name: varchar('name', { length: 120 }),
		eventTypes: text('event_types').array().notNull(),
		filter: jsonb('filter').default({}).notNull(),
		target: jsonb('target').default({}).notNull(),
		// Absent means the full expanded record. Subscriptions narrow, never widen.
		fields: text('fields').array(),
		maxBatchSize: integer('max_batch_size'),
		secretEnc: text('secret_enc'),
		status: integrationRecordSubscriptionStatus('status').default('enabled').notNull(),
		disabledReason: text('disabled_reason'),
		source: varchar('source', { length: 20 }).default('user').notNull(),
		createdByApiKeyId: uuid('created_by_api_key_id'),
		integrationId: uuid('integration_id'),
		consecutiveFailures: integer('consecutive_failures').default(0).notNull(),
		firstFailureAt: timestamp('first_failure_at', { mode: 'string' }),
		circuitOpenedAt: timestamp('circuit_opened_at', { mode: 'string' }),
		circuitProbeAt: timestamp('circuit_probe_at', { mode: 'string' }),
		debugUntil: timestamp('debug_until', { mode: 'string' }),
		createdByUserId: text('created_by_user_id'),
		// Same reason as integrations.deleted_at: the deliveries cascade off this
		// row, so removing it for real removes the record of what it ever sent.
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedByUserId: text('deleted_by_user_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		index('integration_record_subscriptions_dispatch_idx').on(table.status, table.provider),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'integration_record_subscriptions_organization_id_fk'
		}),
		foreignKey({
			columns: [table.integrationConnectionId],
			foreignColumns: [integrationConnections.integrationConnectionId],
			name: 'integration_record_subscriptions_connection_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.integrationId],
			foreignColumns: [integrations.integrationId],
			name: 'integration_record_subscriptions_integration_id_fk'
		})
	]
);

export const integrationRecordDeliveries = pgTable(
	'integration_record_deliveries',
	{
		integrationRecordDeliveryId: uuid('integration_record_delivery_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		integrationRecordEventId: uuid('integration_record_event_id').notNull(),
		integrationRecordSubscriptionId: uuid('integration_record_subscription_id').notNull(),
		correlationId: uuid('correlation_id').notNull(),
		status: integrationRecordDeliveryStatus('status').default('pending').notNull(),
		attemptCount: integer('attempt_count').default(0).notNull(),
		nextAttemptAt: timestamp('next_attempt_at', { mode: 'string' }),
		lastAttemptAt: timestamp('last_attempt_at', { mode: 'string' }),
		deadLetteredAt: timestamp('dead_lettered_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		// The fan-out idempotency seam. An at-least-once relay can replay an event
		// freely because the second insert collapses into this constraint.
		uniqueIndex('integration_record_deliveries_event_subscription_unique').on(
			table.integrationRecordEventId,
			table.integrationRecordSubscriptionId
		),
		index('integration_record_deliveries_due_idx')
			.on(table.nextAttemptAt)
			.where(sql`status IN ('pending', 'failed')`),
		index('integration_record_deliveries_correlation_idx').on(table.correlationId),
		index('integration_record_deliveries_dead_idx')
			.on(table.deadLetteredAt)
			.where(sql`status = 'dead_lettered'`),
		foreignKey({
			columns: [table.integrationRecordEventId],
			foreignColumns: [integrationRecordEvents.integrationRecordEventId],
			name: 'integration_record_deliveries_event_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.integrationRecordSubscriptionId],
			foreignColumns: [integrationRecordSubscriptions.integrationRecordSubscriptionId],
			name: 'integration_record_deliveries_subscription_id_fk'
		}).onDelete('cascade')
	]
);

export const integrationRecordDeliveryAttempts = pgTable(
	'integration_record_delivery_attempts',
	{
		integrationRecordDeliveryAttemptId: uuid('integration_record_delivery_attempt_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		integrationRecordDeliveryId: uuid('integration_record_delivery_id').notNull(),
		attemptNumber: integer('attempt_number').notNull(),
		requestBodySnippet: text('request_body_snippet'),
		requestByteSize: integer('request_byte_size'),
		responseStatus: integer('response_status'),
		responseBodySnippet: text('response_body_snippet'),
		durationMs: integer('duration_ms'),
		errorClass: varchar('error_class', { length: 50 }),
		attemptedAt: timestamp('attempted_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [
		index('integration_record_delivery_attempts_delivery_idx').on(
			table.integrationRecordDeliveryId
		),
		index('integration_record_delivery_attempts_retention_idx').on(table.attemptedAt),
		foreignKey({
			columns: [table.integrationRecordDeliveryId],
			foreignColumns: [integrationRecordDeliveries.integrationRecordDeliveryId],
			name: 'integration_record_delivery_attempts_delivery_id_fk'
		}).onDelete('cascade')
	]
);

export const apiKeys = pgTable(
	'api_keys',
	{
		apiKeyId: uuid('api_key_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		name: varchar('name', { length: 120 }).notNull(),
		environment: apiKeyEnvironment('environment').notNull(),
		// SHA-256 of the secret. The secret itself is never stored: it is returned
		// once at mint and is unrecoverable afterwards.
		keyHash: text('key_hash').notNull(),
		// Enough to recognise a key in a list without being enough to use it.
		keyPrefix: varchar('key_prefix', { length: 32 }).notNull(),
		last4: varchar('last4', { length: 4 }).notNull(),
		scopes: text('scopes').array().notNull(),
		// Touched on use rather than written per request: the value is for "is this
		// key still in use", where hour-granularity is plenty.
		lastUsedAt: timestamp('last_used_at', { mode: 'string' }),
		revokedAt: timestamp('revoked_at', { mode: 'string' }),
		revokedByUserId: text('revoked_by_user_id'),
		createdByUserId: text('created_by_user_id'),
		integrationId: uuid('integration_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [
		// Authentication looks a key up by hash on every request, so this is the
		// hot path and has to be unique as well as fast.
		uniqueIndex('api_keys_hash_unique').on(table.keyHash),
		index('api_keys_organization_idx').on(table.organizationId),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'api_keys_organization_id_fk'
		}),
		foreignKey({
			columns: [table.integrationId],
			foreignColumns: [integrations.integrationId],
			name: 'api_keys_integration_id_fk'
		})
	]
);

// Mandatory once customers hold credentials (spec §13): every mint, revoke and
// rotation is recorded. Append-only - a row is the fact that something happened,
// so nothing here is ever updated or deleted.
export const credentialAuditLog = pgTable(
	'credential_audit_log',
	{
		credentialAuditLogId: uuid('credential_audit_log_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		// What kind of credential: an api_key, or a subscription signing secret.
		credentialType: varchar('credential_type', { length: 40 }).notNull(),
		credentialId: uuid('credential_id'),
		action: varchar('action', { length: 20 }).notNull(),
		actorUserId: text('actor_user_id'),
		detail: jsonb('detail').default({}).notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [
		index('credential_audit_log_org_idx').on(table.organizationId, table.createdAt),
		index('credential_audit_log_credential_idx').on(table.credentialId),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'credential_audit_log_organization_id_fk'
		})
	]
);

export const oauthTokenKind = pgEnum('oauth_token_kind', [
	'authorization_code',
	'access',
	'refresh'
]);


// Absence means available. Deny-by-default would take every connected app away
// from every existing customer the moment it landed, so the row is the off
// switch and the common case needs no row at all.
export const organizationPartnerApps = pgTable(
	'organization_partner_apps',
	{
		organizationPartnerAppId: uuid('organization_partner_app_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		oauthClientId: uuid('oauth_client_id').notNull(),
		available: boolean('available').default(true).notNull(),
		note: text('note'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		uniqueIndex('organization_partner_apps_unique').on(table.organizationId, table.oauthClientId),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'organization_partner_apps_organization_id_fk'
		}),
		foreignKey({
			columns: [table.oauthClientId],
			foreignColumns: [oauthClients.oauthClientId],
			name: 'organization_partner_apps_client_id_fk'
		})
	]
);

// The catalog for apps Sunfish calls with a key the customer gives us, the other
// way round from oauth_clients, where the app calls Sunfish. A row is only ever
// offered when code also has an adapter and a provider definition for its key,
// so seeding a row cannot put a tile on the add page that cannot work.
export const integrationProviders = pgTable('integration_providers', {
	providerKey: varchar('provider_key', { length: 64 }).primaryKey().notNull(),
	kind: varchar('kind', { length: 32 }).notNull(),
	name: varchar('name', { length: 120 }).notNull(),
	description: text('description'),
	logoUrl: text('logo_url'),
	defaultAvailable: boolean('default_available').default(true).notNull(),
	disabledAt: timestamp('disabled_at', { mode: 'string' }),
	createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
	updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
});

// Absence falls back to the provider's default_available, as organization_partner_apps does.
export const organizationIntegrationProviders = pgTable(
	'organization_integration_providers',
	{
		organizationIntegrationProviderId: uuid('organization_integration_provider_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		providerKey: varchar('provider_key', { length: 64 }).notNull(),
		available: boolean('available').default(true).notNull(),
		note: text('note'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		uniqueIndex('organization_integration_providers_unique').on(table.organizationId, table.providerKey),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'organization_integration_providers_organization_id_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.providerKey],
			foreignColumns: [integrationProviders.providerKey],
			name: 'organization_integration_providers_provider_key_fk'
		}).onDelete('cascade')
	]
);

// The grant, not the token, is the durable identity: it becomes credentialId on
// a partner principal and is what echo suppression, /v1/hooks ownership and rate
// limiting all key on. Access tokens refresh hourly against a stable grant id.
export const oauthGrants = pgTable(
	'oauth_grants',
	{
		oauthGrantId: uuid('oauth_grant_id').default(sql`gen_random_uuid()`).primaryKey().notNull(),
		oauthClientId: uuid('oauth_client_id').notNull(),
		organizationId: text('organization_id').notNull(),
		approvedByUserId: text('approved_by_user_id'),
		integrationId: uuid('integration_id'),
		scopes: text('scopes').array().notNull(),
		environment: apiKeyEnvironment('environment').notNull(),
		lastUsedAt: timestamp('last_used_at', { mode: 'string' }),
		revokedAt: timestamp('revoked_at', { mode: 'string' }),
		revokedByUserId: text('revoked_by_user_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [
		index('oauth_grants_org_idx').on(table.organizationId, table.oauthClientId),
		index('oauth_grants_client_idx').on(table.oauthClientId, table.revokedAt),
		// One live grant per client/org/approver, so a re-consent updates the
		// connection instead of stacking a second one the customer cannot revoke.
		// Revoked rows are excluded so history survives and reconnecting works.
		uniqueIndex('oauth_grants_live_unique')
			.on(table.oauthClientId, table.organizationId, table.approvedByUserId)
			.where(sql`revoked_at IS NULL`),
		foreignKey({
			columns: [table.oauthClientId],
			foreignColumns: [oauthClients.oauthClientId],
			name: 'oauth_grants_client_id_fk'
		}),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'oauth_grants_organization_id_fk'
		}),
		foreignKey({
			columns: [table.integrationId],
			foreignColumns: [integrations.integrationId],
			name: 'oauth_grants_integration_id_fk'
		})
	]
);

export const oauthTokens = pgTable(
	'oauth_tokens',
	{
		oauthTokenId: uuid('oauth_token_id').default(sql`gen_random_uuid()`).primaryKey().notNull(),
		oauthGrantId: uuid('oauth_grant_id').notNull(),
		kind: oauthTokenKind('kind').notNull(),
		// SHA-256 of the token. The token itself is never stored: it is returned
		// once at issuance and is unrecoverable afterwards.
		tokenHash: text('token_hash').notNull(),
		// Authorization-code fields. Null for access and refresh tokens.
		redirectUri: text('redirect_uri'),
		codeChallenge: text('code_challenge'),
		expiresAt: timestamp('expires_at', { mode: 'string' }).notNull(),
		redeemedAt: timestamp('redeemed_at', { mode: 'string' }),
		replacedByTokenId: uuid('replaced_by_token_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [
		uniqueIndex('oauth_tokens_hash_unique').on(table.tokenHash),
		index('oauth_tokens_grant_idx').on(table.oauthGrantId, table.kind),
		foreignKey({
			columns: [table.oauthGrantId],
			foreignColumns: [oauthGrants.oauthGrantId],
			name: 'oauth_tokens_grant_id_fk'
		})
	]
);

// Lets an external system address a Sunfish record by its own id, and lets us
// recognise a record we have already synced. One generic table for every
// provider, the same way the delivery tables are: new tables per plane were
// always expected, new tables per provider were not.
//
// Built before writes need it because retrofitting identity onto an established
// integration means re-mapping every customer's records.
export const integrationInboundWriteOutcome = pgEnum('integration_inbound_write_outcome', [
	'accepted',
	'rejected',
	'failed'
]);

export const integrationInboundWrites = pgTable(
	'integration_inbound_writes',
	{
		integrationInboundWriteId: uuid('integration_inbound_write_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		// Who wrote. Deliberately not a foreign key to api_keys: an OAuth caller's
		// credential is a grant, not a key, so a key-shaped constraint would refuse
		// every write a partner app makes. The log also has to outlive whatever it
		// names - a revoked credential is exactly when its history matters.
		credentialKind: varchar('credential_kind', { length: 20 }).notNull(),
		credentialId: uuid('credential_id'),
		// Best effort, for the name shown in the list. Null is readable: the row
		// still says which resource was written and why it was refused.
		integrationId: uuid('integration_id'),
		resource: varchar('resource', { length: 50 }).notNull(),
		operation: varchar('operation', { length: 20 }).notNull(),
		// Set only when something was written. A refused write has no record.
		recordId: text('record_id'),
		// The event the record belongs to, when it belongs to one. A contact does
		// not, which is exactly the case this table exists to explain.
		eventId: uuid('event_id'),
		outcome: integrationInboundWriteOutcome('outcome').notNull(),
		statusCode: integer('status_code').notNull(),
		errorCode: varchar('error_code', { length: 64 }),
		// Why it was refused, in the words the caller was given. This is the answer
		// to "I added a contact, why is it not there" - without it the row says
		// something was rejected and leaves the reason on a Zapier run page.
		message: text('message'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [
		index('integration_inbound_writes_org_created_idx').on(
			table.organizationId,
			table.createdAt
		),
		// The event page reads by event; a null event id (an organization-wide
		// record) is deliberately not in this index's reach.
		index('integration_inbound_writes_event_idx')
			.on(table.eventId, table.createdAt)
			.where(sql`event_id IS NOT NULL`),
		foreignKey({
			columns: [table.integrationId],
			foreignColumns: [integrations.integrationId],
			name: 'integration_inbound_writes_integration_id_fk'
		}).onDelete('set null')
	]
);

export const integrationRecordIdentities = pgTable(
	'integration_record_identities',
	{
		integrationRecordIdentityId: uuid('integration_record_identity_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		provider: varchar('provider', { length: 64 }).notNull(),
		// Null for a connectionless provider: a plain customer webhook has one
		// identity space per organization rather than per connection.
		integrationConnectionId: uuid('integration_connection_id'),
		resource: varchar('resource', { length: 50 }).notNull(),
		sunfishId: text('sunfish_id').notNull(),
		externalId: text('external_id').notNull(),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' }).$onUpdate(() => sql`CURRENT_TIMESTAMP`)
	},
	(table) => [
		// Both directions are lookups, and both must be unique within a provider's
		// identity space: one external record maps to one Sunfish record and back.
		// Two partial indexes rather than one, because NULLs are distinct in a
		// unique index and a connectionless provider would otherwise never conflict
		// with itself - the same trap integration_connections already documents.
		uniqueIndex('integration_record_identities_external_conn_unique')
			.on(table.provider, table.integrationConnectionId, table.resource, table.externalId)
			.where(sql`integration_connection_id IS NOT NULL`),
		uniqueIndex('integration_record_identities_external_org_unique')
			.on(table.organizationId, table.provider, table.resource, table.externalId)
			.where(sql`integration_connection_id IS NULL`),
		uniqueIndex('integration_record_identities_sunfish_conn_unique')
			.on(table.provider, table.integrationConnectionId, table.resource, table.sunfishId)
			.where(sql`integration_connection_id IS NOT NULL`),
		uniqueIndex('integration_record_identities_sunfish_org_unique')
			.on(table.organizationId, table.provider, table.resource, table.sunfishId)
			.where(sql`integration_connection_id IS NULL`),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'integration_record_identities_organization_id_fk'
		}),
		foreignKey({
			columns: [table.integrationConnectionId],
			foreignColumns: [integrationConnections.integrationConnectionId],
			name: 'integration_record_identities_connection_id_fk'
		}).onDelete('cascade')
	]
);

export const integrationSyncRunStatus = pgEnum('integration_sync_run_status', [
	'pending',
	'running',
	'succeeded',
	'partial',
	'failed',
	'cancelled'
]);

export const integrationSyncCadence = pgEnum('integration_sync_cadence', ['manual', 'hourly', 'daily']);

export const integrationSyncConfigs = pgTable(
	'integration_sync_configs',
	{
		integrationSyncConfigId: uuid('integration_sync_config_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		organizationId: text('organization_id').notNull(),
		integrationId: uuid('integration_id').notNull(),
		integrationConnectionId: uuid('integration_connection_id').notNull(),
		provider: text('provider').notNull(),
		eventId: uuid('event_id').notNull(),
		targetContactListId: uuid('target_contact_list_id').notNull(),
		lapsedContactListId: uuid('lapsed_contact_list_id'),
		// [{ sourceSegmentId, sourceSegmentLabel, kind }]
		segments: jsonb('segments').notNull().default([]),
		// [{ sourceKey, target: { kind: 'core'|'attribute'|'custom'|'form_field', name } }]
		fieldMappings: jsonb('field_mappings').notNull().default([]),
		cadence: integrationSyncCadence('cadence').notNull().default('daily'),
		status: integrationStatus('status').notNull().default('active'),
		disabledReason: text('disabled_reason'),
		lastRunAt: timestamp('last_run_at', { mode: 'string' }),
		nextRunAt: timestamp('next_run_at', { mode: 'string' }),
		consecutiveFailures: integer('consecutive_failures').notNull().default(0),
		firstFailureAt: timestamp('first_failure_at', { mode: 'string' }),
		circuitOpenedAt: timestamp('circuit_opened_at', { mode: 'string' }),
		circuitProbeAt: timestamp('circuit_probe_at', { mode: 'string' }),
		// The first run that read the whole roster and applied it. Until then the
		// sync is importing, not reporting changes, so it emits no member events.
		firstCompletedRunAt: timestamp('first_completed_run_at', { mode: 'string' }),
		createdByUserId: text('created_by_user_id'),
		deletedAt: timestamp('deleted_at', { mode: 'string' }),
		deletedByUserId: text('deleted_by_user_id'),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
		updatedAt: timestamp('updated_at', { mode: 'string' })
	},
	(table) => [
		// Partial so a deleted sync frees its list to be synced again.
		uniqueIndex('integration_sync_configs_target_unique')
			.on(table.targetContactListId)
			.where(sql`${table.deletedAt} is null`),
		index('integration_sync_configs_due_idx').on(table.nextRunAt, table.status),
		index('integration_sync_configs_org_idx').on(table.organizationId),
		foreignKey({
			columns: [table.integrationId],
			foreignColumns: [integrations.integrationId],
			name: 'integration_sync_configs_integration_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.integrationConnectionId],
			foreignColumns: [integrationConnections.integrationConnectionId],
			name: 'integration_sync_configs_connection_fk'
		}),
		foreignKey({
			columns: [table.eventId],
			foreignColumns: [events.eventId],
			name: 'integration_sync_configs_event_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.targetContactListId],
			foreignColumns: [contactLists.contactListId],
			name: 'integration_sync_configs_target_list_fk'
		}).onDelete('cascade'),
		foreignKey({
			columns: [table.lapsedContactListId],
			foreignColumns: [contactLists.contactListId],
			name: 'integration_sync_configs_lapsed_list_fk'
		}).onDelete('set null')
	]
);

export const integrationSyncRuns = pgTable(
	'integration_sync_runs',
	{
		integrationSyncRunId: uuid('integration_sync_run_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		integrationSyncConfigId: uuid('integration_sync_config_id').notNull(),
		organizationId: text('organization_id').notNull(),
		correlationId: uuid('correlation_id').notNull(),
		status: integrationSyncRunStatus('status').notNull().default('pending'),
		trigger: text('trigger').notNull(), // 'cron' | 'manual' | 'webhook_hint'
		// Resume point, so a rate-limited run continues rather than restarting.
		cursor: jsonb('cursor'),
		pagesFetched: integer('pages_fetched').notNull().default(0),
		recordsSeen: integer('records_seen').notNull().default(0),
		membersAdded: integer('members_added').notNull().default(0),
		membersRemoved: integer('members_removed').notNull().default(0),
		membersUpdated: integer('members_updated').notNull().default(0),
		recordsSkipped: integer('records_skipped').notNull().default(0),
		// [{ reason, count, sample: [...] }] — e.g. missing email, unmapped segment.
		skipReasons: jsonb('skip_reasons').notNull().default([]),
		// Mapping rows whose source field no longer exists. Reported once per run.
		mappingWarnings: jsonb('mapping_warnings').notNull().default([]),
		errorClass: text('error_class'),
		errorMessage: text('error_message'),
		startedAt: timestamp('started_at', { mode: 'string' }),
		finishedAt: timestamp('finished_at', { mode: 'string' }),
		createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [
		index('integration_sync_runs_config_idx').on(table.integrationSyncConfigId, table.createdAt),
		foreignKey({
			columns: [table.integrationSyncConfigId],
			foreignColumns: [integrationSyncConfigs.integrationSyncConfigId],
			name: 'integration_sync_runs_config_fk'
		}).onDelete('cascade')
	]
);

export const integrationSyncRunAttempts = pgTable(
	'integration_sync_run_attempts',
	{
		integrationSyncRunAttemptId: uuid('integration_sync_run_attempt_id')
			.default(sql`gen_random_uuid()`)
			.primaryKey()
			.notNull(),
		integrationSyncRunId: uuid('integration_sync_run_id').notNull(),
		attemptNumber: integer('attempt_number').notNull(),
		requestSummary: text('request_summary'),
		responseStatus: integer('response_status'),
		responseBodySnippet: text('response_body_snippet'),
		durationMs: integer('duration_ms'),
		errorClass: text('error_class'),
		attemptedAt: timestamp('attempted_at', { mode: 'string' }).defaultNow().notNull()
	},
	(table) => [
		index('integration_sync_run_attempts_run_idx').on(table.integrationSyncRunId),
		foreignKey({
			columns: [table.integrationSyncRunId],
			foreignColumns: [integrationSyncRuns.integrationSyncRunId],
			name: 'integration_sync_run_attempts_run_fk'
		}).onDelete('cascade')
	]
);
